import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  IconButton,
  Grid,
  Autocomplete,
  CircularProgress,
  Portal,
} from '@mui/material';
import {
  Close,
  AttachMoney,
  CreditCard,
  AccountBalance,
  CallSplit,
  Person,
  TableRestaurant,
  ChevronRight,
  Delete,
  Add,
  QrCode2,
  Check,
} from '@mui/icons-material';
import SplitTableDialog from './SplitTableDialog';
import CustomerDataService from '../services/customerDataService';
import CheckoutDraftService, { CheckoutDraft } from '../services/checkoutDraftService';
import { firebaseTableService } from '../services/firebaseTableService';
import { ReservationCloudService } from '../services/restaurantCloudService';
import { useLanguage } from '../contexts/LanguageContext';
import { useInstantTheme } from '../contexts/InstantThemeContext';
import GoogleDeliveryMap from './GoogleDeliveryMap';
import { loadDeliveryZone, quoteDelivery, DeliveryZone, DeliveryQuote } from '../services/deliveryZones';
import PayPalService from '../services/paypalService';

interface CheckoutDialogProps {
  open: boolean;
  onClose: () => void;
  cart: any[];
  onCheckout: (checkoutData: CheckoutData) => Promise<void>;
  tableContext?: {
    id?: string;
    number: number; // Database number (for operations)
    display_number?: number; // Sequential display number (for UI)
    type: string;
    area?: string;
    customer_name?: string;
    party_size?: number;
    start_time?: string;
    active_order_id?: string;
    active_order_number?: string;
  } | null;
  draftData?: CheckoutDraft | null;
  tableExistingCustomerName?: string;
  // When set (by "+ Add Items" flow), the dialog auto-selects this order type
  // and disables switching away would be confusing — we keep switching allowed
  // but start from the correct default so staff don't confirm dine-in on a pickup.
  forcedOrderType?: 'dine_in' | 'takeaway' | 'delivery';
  forcedCustomerName?: string;
  onPayPalOrderCreated?: (orderId: string, approvalUrl: string, amount: number, originalCartItems?: any[], orderType?: 'dine_in' | 'takeaway' | 'delivery') => void;
}

export interface CheckoutData {
  customer_name: string;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_phone: string;
  customer_address?: string;
  customer_postcode?: string;
  customer_city?: string;
  delivery_latitude?: number;
  delivery_longitude?: number;
  table_number?: number | null;
  table_area?: 'indoor' | 'outdoor' | null; // CRITICAL: Area to ensure correct table matching
  order_type: 'dine_in' | 'takeaway' | 'delivery';
  payment_method: 'cash' | 'card' | 'paypal';
  payment_status: 'pending' | 'completed';
  use_split_payment: boolean;
  delivery_fee?: number;
  // Discount applied at checkout (works for dine-in, takeaway, delivery)
  discount_type?: 'percentage' | 'fixed';
  discount_value?: number;
  discount_amount?: number;
  discount_reason?: string;
  total_before_discount?: number;
}

const CheckoutDialog: React.FC<CheckoutDialogProps> = ({
  open,
  onClose,
  cart,
  onCheckout,
  tableContext,
  draftData,
  tableExistingCustomerName,
  forcedOrderType,
  forcedCustomerName,
  onPayPalOrderCreated,
}) => {
  // CRITICAL: Log every time CheckoutDialog renders
  console.log('🔄 [CHECKOUT DIALOG] Component function called/re-rendered');
  
  const { t, language } = useLanguage();
  const { appTheme } = useInstantTheme();
  const checkoutDraftService = CheckoutDraftService.getInstance();
  const [orderTypeMenuAnchor, setOrderTypeMenuAnchor] = useState<null | HTMLElement>(null);
  const [orderTypeMenuPosition, setOrderTypeMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [customerMenuAnchor, setCustomerMenuAnchor] = useState<null | HTMLElement>(null);
  const [customerMenuPosition, setCustomerMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [customerPostcode, setCustomerPostcode] = useState('');
  const [customerCity, setCustomerCity] = useState('');
  const [tableNumber, setTableNumber] = useState<number | null>(null);
  const [tableDatabaseNumber, setTableDatabaseNumber] = useState<number | null>(null); // Store actual database number for operations
  const [orderType, setOrderType] = useState<'dine_in' | 'takeaway' | 'delivery'>('dine_in');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'paypal'>('cash');
  const [useSplitPayment, setUseSplitPayment] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [customerListDialogOpen, setCustomerListDialogOpen] = useState(false);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [draftSaved, setDraftSaved] = useState(false); // Track if draft was already saved
  
  // PayPal error state (only for displaying errors in CheckoutDialog)
  const [paypalError, setPaypalError] = useState<string | null>(null);
  const paypalService = PayPalService.getInstance();
  
  // Restaurant tables for visual table selector
  const [allTables, setAllTables] = useState<any[]>([]);
  const [selectedTableArea, setSelectedTableArea] = useState<string | null>(null);
  const [tableWarning, setTableWarning] = useState<{ show: boolean; table: any; area: string } | null>(null);

  // Add customer form state
  const [newCustomerFirstName, setNewCustomerFirstName] = useState('');
  const [newCustomerLastName, setNewCustomerLastName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [newCustomerAddress, setNewCustomerAddress] = useState('');
  const [newCustomerPostcode, setNewCustomerPostcode] = useState('');
  const [newCustomerCity, setNewCustomerCity] = useState('');
  
  // Customer autocomplete
  const [customerSuggestions, setCustomerSuggestions] = useState<any[]>([]);
  const [allCustomers, setAllCustomers] = useState<any[]>([]);
  const customerService = CustomerDataService.getInstance();
  
  // Google Maps address autocomplete
  interface AddressSuggestion {
    address: string;
    street: string;
    city: string;
    postcode: string;
    state: string;
    lat?: number;
    lon?: number;
  }
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [deliveryLat, setDeliveryLat] = useState<number | undefined>(undefined);
  const [deliveryLng, setDeliveryLng] = useState<number | undefined>(undefined);
  const [deliveryZone, setDeliveryZone] = useState<DeliveryZone | null>(null);
  const [zoneLoading, setZoneLoading] = useState(false);
  const [zoneLookedUp, setZoneLookedUp] = useState(false);

  // Discount state — works for dine-in, takeaway, and delivery
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState<string>('');
  const [discountReason, setDiscountReason] = useState<string>('');
  const [discountApplied, setDiscountApplied] = useState<boolean>(false);

  // Calculate totals with proper tax calculation based on order type and item category
  // Rules: Takeaway = 7%, Dine-in = 19%, Drinks always = 19%
  // VAT is included in item price, so we calculate base price from total
  const cartTotals = React.useMemo(() => {
    let subtotal7 = 0;
    let subtotal19 = 0;
    let tax7 = 0;
    let tax19 = 0;
    
    cart.forEach(item => {
      const itemTotal = item.price * item.quantity; // price_with_tax already includes VAT
      
      // CRITICAL: Check if item is a drink (always 19% VAT in Germany - TSE rule)
      // German TSE rules: ALL drinks = 19% VAT regardless of order type (dine-in, takeaway, delivery)
      // and regardless of payment method (cash, card, PayPal)
      const categoryName = (item.category_name || '').toLowerCase();
      const itemNameLower = (item.name || '').toLowerCase();
      const isDrink = categoryName.includes('drink') || 
                      categoryName.includes('getränk') || 
                      categoryName.includes('beverage') ||
                      categoryName.includes('getränke') ||
                      itemNameLower.includes('cola') ||
                      itemNameLower.includes('coca') ||
                      itemNameLower.includes('pepsi') ||
                      itemNameLower.includes('sprite') ||
                      itemNameLower.includes('fanta') ||
                      itemNameLower.includes('wasser') ||
                      itemNameLower.includes('water') ||
                      itemNameLower.includes('saft') ||
                      itemNameLower.includes('juice') ||
                      itemNameLower.includes('limo') ||
                      itemNameLower.includes('softdrink') ||
                      itemNameLower.includes('bier') ||
                      itemNameLower.includes('beer') ||
                      itemNameLower.includes('wein') ||
                      itemNameLower.includes('wine') ||
                      itemNameLower.includes('kaffee') ||
                      itemNameLower.includes('coffee') ||
                      itemNameLower.includes('tee') ||
                      itemNameLower.includes('tea');
      
      // Determine tax rate:
      // CRITICAL GERMAN TSE RULES:
      // - Drinks: ALWAYS 19% VAT (regardless of order type: dine-in, takeaway, delivery)
      // - Drinks: ALWAYS 19% VAT (regardless of payment method: cash, card, PayPal)
      // - Food (Takeaway/Delivery): 7% VAT
      // - Food (Dine-in): 19% VAT
      let taxRate: number;
      if (isDrink) {
        taxRate = 19; // CRITICAL: Drinks ALWAYS 19% VAT in Germany (TSE rule)
      } else {
        // Use order type from checkout dialog
        taxRate = (orderType === 'takeaway' || orderType === 'delivery') ? 7 : 19;
      }
      
      // Calculate base price (without tax) from price_with_tax
      // VAT is included in price, so: basePrice = total / (1 + VAT%)
      // Example: Chicken-Crispy Roll €10.90:
      //   Takeaway (7%): basePrice = 10.90 / 1.07 = 10.19, tax = 10.90 - 10.19 = 0.71
      //   Dine-in (19%): basePrice = 10.90 / 1.19 = 9.16, tax = 10.90 - 9.16 = 1.74
      const basePrice = itemTotal / (1 + taxRate / 100);
      const itemTax = itemTotal - basePrice;
      
      // Debug logging for each item
      if (cart.length <= 3) { // Only log for small carts to avoid spam
        console.log(`💰 [TAX CALC] ${item.name}:`, {
          price_with_tax: itemTotal.toFixed(2),
          tax_rate: `${taxRate}%`,
          base_price: basePrice.toFixed(2),
          tax_amount: itemTax.toFixed(2),
          order_type: orderType,
          is_drink: isDrink
        });
      }
      
      if (taxRate === 7) {
        subtotal7 += basePrice;
        tax7 += itemTax;
      } else if (taxRate === 19) {
        subtotal19 += basePrice;
        tax19 += itemTax;
      } else {
        // Fallback to 7% for unknown tax rates
        subtotal7 += basePrice;
        tax7 += itemTax;
      }
    });
    
    return {
      subtotal: subtotal7 + subtotal19,
      tax: tax7 + tax19,
      total: subtotal7 + subtotal19 + tax7 + tax19,
      subtotal7,
      tax7,
      subtotal19,
      tax19
    };
  }, [cart, orderType]);

  const taxAmount = cartTotals.tax;
  const subtotal = cartTotals.subtotal;
  const total = cartTotals.total;

  // Delivery zone lookup: fetch zone whenever postcode + delivery order type.
  // Using `total` (VAT-incl items) as the amount checked against min_order / free_threshold
  // so the thresholds match what the customer actually pays for food.
  useEffect(() => {
    if (orderType !== 'delivery') {
      setDeliveryZone(null);
      setZoneLookedUp(false);
      return;
    }
    const zip = (customerPostcode || '').trim();
    if (zip.length < 5) {
      setDeliveryZone(null);
      setZoneLookedUp(false);
      return;
    }
    let cancelled = false;
    setZoneLoading(true);
    loadDeliveryZone(zip).then((zone) => {
      if (cancelled) return;
      setDeliveryZone(zone);
      setZoneLookedUp(true);
      setZoneLoading(false);
    });
    return () => { cancelled = true; };
  }, [customerPostcode, orderType]);

  const deliveryQuote: DeliveryQuote = React.useMemo(
    () => quoteDelivery(deliveryZone, total),
    [deliveryZone, total],
  );
  const deliveryFee = orderType === 'delivery' ? deliveryQuote.fee : 0;
  const totalBeforeDiscount = total + deliveryFee;

  // Discount amount: capped at total, non-negative
  const discountAmount = React.useMemo(() => {
    if (!discountApplied) return 0;
    const v = parseFloat(discountValue);
    if (isNaN(v) || v <= 0) return 0;
    const raw = discountType === 'percentage'
      ? totalBeforeDiscount * (v / 100)
      : v;
    return Math.min(Math.max(0, raw), totalBeforeDiscount);
  }, [discountApplied, discountType, discountValue, totalBeforeDiscount]);

  const totalWithFee = Math.max(0, totalBeforeDiscount - discountAmount);
  // When adding items to an existing order, skip minimum-order enforcement —
  // the original order already met the threshold.
  const isAddingToExistingOrder = typeof window !== 'undefined'
    && !!localStorage.getItem('saheeh_edit_order_id');
  const deliveryBlocked = orderType === 'delivery'
    && zoneLookedUp
    && !deliveryQuote.allowed
    && !isAddingToExistingOrder;

  // Load draft data or table context when dialog opens
  useEffect(() => {
    if (open) {
      // If resuming from draft, load draft data
      if (draftData) {
        console.log('📋 [CHECKOUT] Resuming from draft:', draftData);
        setCurrentDraftId(draftData.draft_id || null);
        setDraftSaved(true); // Mark as already saved since we're resuming
        
        // Restore cart items (if needed - cart is passed as prop)
        // Restore form fields
        if (draftData.customer_name) setCustomerName(draftData.customer_name);
        if (draftData.customer_first_name) setCustomerFirstName(draftData.customer_first_name);
        if (draftData.customer_last_name) setCustomerLastName(draftData.customer_last_name);
        if (draftData.customer_phone) setCustomerPhone(draftData.customer_phone);
        if (draftData.customer_address) setCustomerAddress(draftData.customer_address);
        if (draftData.customer_postcode) setCustomerPostcode(draftData.customer_postcode);
        if (draftData.customer_city) setCustomerCity(draftData.customer_city);
        if (draftData.table_number) setTableNumber(draftData.table_number);
        if (draftData.order_type) setOrderType(draftData.order_type);
        if (draftData.payment_method) {
          const method = draftData.payment_method;
          if (method === 'cash' || method === 'card' || method === 'paypal') {
            setPaymentMethod(method);
          } else {
            setPaymentMethod('cash');
          }
        }
      } else if (tableContext) {
        // If coming from restaurant page, pre-fill table info and check for existing draft
        console.log('🪑 [CHECKOUT] Loading table context:', tableContext);
        // Always show the REAL physical table number — display_number is now
        // mirrored from .number, but prefer .number directly to defeat any stale
        // sequential index left in localStorage from before the fix.
        const dbNum: number | null = tableContext.number ?? tableContext.display_number ?? null;
        setTableNumber(dbNum); // Show real table number in input field
        setTableDatabaseNumber(dbNum); // Store database number for saving
        setOrderType('dine_in');
        // Pre-fill customer name ONLY when adding to an existing occupied table with active orders
        // For NEW orders on a table, leave customer name empty (user can type it if they want)
        const hasActiveOrders = tableContext.active_order_id || tableContext.active_order_number;
        if (hasActiveOrders) {
          if (tableContext.customer_name) {
            setCustomerName(tableContext.customer_name);
          } else if (tableExistingCustomerName) {
            setCustomerName(tableExistingCustomerName);
          }
        }
        
        // Check if draft already exists for this table (use database number)
        if (dbNum === null) {
          // No table number to query against — skip draft lookup
          return;
        }
        checkoutDraftService.getActiveDraftByTable(dbNum).then(existingDraft => {
          if (existingDraft) {
            console.log('✅ [CHECKOUT] Found existing draft for table:', existingDraft);
            setCurrentDraftId(existingDraft.draft_id || null);
            setDraftSaved(true); // Mark as already saved
          }
        }).catch(error => {
          console.error('❌ [CHECKOUT] Error checking for existing draft:', error);
        });
      } else {
        // Reset to defaults when dialog opens fresh.
        // If the parent passed a forcedOrderType (e.g. "+ Add Items" on a takeaway
        // order), honor it so staff don't accidentally confirm the wrong type.
        setOrderType(forcedOrderType || 'dine_in');
        setPaymentMethod('cash');
        setTableNumber(null);
        setTableDatabaseNumber(null);
        setUseSplitPayment(false);
        setAddressSuggestions([]);
        setDraftSaved(false);
        setCurrentDraftId(null);
        if (forcedCustomerName) setCustomerName(forcedCustomerName);
      }
    } else {
      // Clear state when dialog closes
      setShowCloseConfirm(false);
      setCurrentDraftId(null);
      setDraftSaved(false);
    }
  }, [open, draftData, tableContext, tableExistingCustomerName, checkoutDraftService]);

  // DINE-IN: Auto-fill customer name when tableExistingCustomerName becomes available
  // Only when adding to an existing table with active orders (not for new orders)
  useEffect(() => {
    const hasActiveOrders = tableContext?.active_order_id || tableContext?.active_order_number;
    if (open && hasActiveOrders && tableExistingCustomerName && !customerName && orderType === 'dine_in') {
      console.log('🪑 [CHECKOUT] Auto-filling customer name from table orders:', tableExistingCustomerName);
      setCustomerName(tableExistingCustomerName);
    }
  }, [open, tableExistingCustomerName, customerName, orderType, tableContext]);

  // DINE-IN: Auto-fill from tableContext.customer_name when it updates
  useEffect(() => {
    if (open && tableContext?.customer_name && !customerName && orderType === 'dine_in') {
      console.log('🪑 [CHECKOUT] Auto-filling customer name from table context:', tableContext.customer_name);
      setCustomerName(tableContext.customer_name);
    }
  }, [open, tableContext?.customer_name, customerName, orderType]);

  // Load restaurant tables for visual selector when dine-in is selected
  useEffect(() => {
    if (open && orderType === 'dine_in') {
      // Helper: merge reservation statuses into tables
      const mergeReservations = (tables: any[], reservations: any[]) => {
        const today = new Date().toISOString().split('T')[0];
        const todayReservations = reservations.filter(r => {
          try {
            let resTime: Date;
            if (typeof r.reservation_time === 'string') {
              resTime = new Date(r.reservation_time);
            } else if (r.reservation_time?.toDate) {
              resTime = r.reservation_time.toDate();
            } else {
              return false;
            }
            return resTime.toISOString().split('T')[0] === today && r.status === 'confirmed';
          } catch { return false; }
        });
        if (todayReservations.length === 0) return tables;
        return tables.map(table => {
          const hasReservation = todayReservations.find(res => res.table_number === table.number);
          if (hasReservation && table.status !== 'occupied') {
            return { ...table, status: 'reserved', customer_name: hasReservation.customer_name || table.customer_name };
          }
          return table;
        });
      };

      let currentReservations: any[] = [];
      let currentTables: any[] = [];

      const loadInitial = async () => {
        try {
          const [tables, reservations] = await Promise.all([
            firebaseTableService.getTables(),
            ReservationCloudService.getReservations()
          ]);
          currentTables = tables;
          currentReservations = reservations;
          setAllTables(mergeReservations(tables, reservations));
        } catch (error) {
          console.error('Failed to load tables:', error);
        }
      };
      loadInitial();

      // Real-time listener for table changes
      const unsubTables = firebaseTableService.onTablesChange((tables) => {
        currentTables = tables;
        setAllTables(mergeReservations(tables, currentReservations));
      });
      // Real-time listener for reservation changes
      const unsubReservations = ReservationCloudService.onReservationsChange((reservations) => {
        currentReservations = reservations;
        setAllTables(mergeReservations(currentTables, reservations));
      });
      return () => { unsubTables(); unsubReservations(); };
    }
  }, [open, orderType]);

  // Reset form when dialog closes (after draft is saved)
  useEffect(() => {
    if (!open) {
      // Clear customer fields when dialog closes
      setCustomerName('');
      setCustomerFirstName('');
      setCustomerLastName('');
      setCustomerPhone('');
      setCustomerAddress('');
      setCustomerPostcode('');
      setCustomerCity('');
      setDeliveryLat(undefined);
      setDeliveryLng(undefined);
    }
  }, [open]);

  // Google Maps Geocoding API for address autocomplete (Germany)
  const searchAddress = async (postcode: string) => {
    if (!postcode || postcode.length < 5) {
      setAddressSuggestions([]);
      return;
    }

    try {
      setLoadingAddress(true);
      // Use OpenStreetMap Nominatim API for German postal code lookup
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?postalcode=${postcode}&country=Germany&format=json&limit=5`
      );
      
      if (response.ok) {
        const data = await response.json();
        const suggestions = data.map((item: any) => ({
          address: item.display_name,
          street: item.address?.road || '',
          city: item.address?.city || item.address?.town || item.address?.village || '',
          postcode: item.address?.postcode || postcode,
          state: item.address?.state || '',
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
        }));
        setAddressSuggestions(suggestions);
        
        // Auto-fill address if only one suggestion
        if (suggestions.length === 1) {
          const suggestion = suggestions[0];
          // Only update fields that are missing - preserve existing values
          if (!customerAddress && (suggestion.street || suggestion.address)) {
            setCustomerAddress(suggestion.street || suggestion.address || '');
          }
          if (!customerPostcode && suggestion.postcode) {
            setCustomerPostcode(suggestion.postcode);
          }
          if (!customerCity && suggestion.city) {
            setCustomerCity(suggestion.city);
          }
          if (suggestion.lat && suggestion.lon) {
            setDeliveryLat(suggestion.lat);
            setDeliveryLng(suggestion.lon);
          }
        }
      }
    } catch (error) {
      console.error('❌ [ADDRESS] Error fetching address:', error);
    } finally {
      setLoadingAddress(false);
    }
  };

  // Geocode full address to get coordinates
  const geocodeAddress = async (address: string, postcode: string, city: string) => {
    console.log('📍 [GEOCODE] ========== STARTING GEOCODING ==========');
    console.log('📍 [GEOCODE] Input parameters:', { address, postcode, city });

    // Use defaults if missing
    const finalPostcode = postcode || '50354';
    const finalCity = city || 'Hürth';
    const finalAddress = address || '';

    console.log('📍 [GEOCODE] Final values:', { finalPostcode, finalCity, finalAddress });

    if (!finalPostcode || !finalCity) {
      console.warn('⚠️ [GEOCODE] Missing required fields - cannot geocode');
      setDeliveryLat(undefined);
      setDeliveryLng(undefined);
      return;
    }

    try {
      // PRIMARY: Google Maps JS Geocoder (works with referer-restricted keys).
      // Wait for the Maps JS API to finish loading (up to ~8s) so this runs
      // even if the checkout dialog triggers geocoding before the map mounts.
      const waitForGoogle = async (): Promise<any> => {
        const maxWaitMs = 8000;
        const start = Date.now();
        while (Date.now() - start < maxWaitMs) {
          const g = (window as any).google;
          if (g && g.maps && g.maps.Geocoder) return g;
          await new Promise((r) => setTimeout(r, 150));
        }
        return null;
      };
      const google = await waitForGoogle();
      if (google) {
        try {
          const geocoder = new google.maps.Geocoder();
          const fullAddress = finalAddress
            ? `${finalAddress}, ${finalPostcode} ${finalCity}, Germany`
            : `${finalPostcode} ${finalCity}, Germany`;
          console.log('🌍 [GEOCODE] Google JS Geocoder request:', fullAddress);
          const gResult: any = await new Promise((resolve) => {
            geocoder.geocode(
              {
                address: fullAddress,
                region: 'de',
                componentRestrictions: { country: 'DE' },
              },
              (results: any, status: any) => resolve({ results, status })
            );
          });
          if (gResult.status === 'OK' && gResult.results && gResult.results.length > 0) {
            const ranked = [...gResult.results].sort((a: any, b: any) => {
              const order = ['ROOFTOP', 'RANGE_INTERPOLATED', 'GEOMETRIC_CENTER', 'APPROXIMATE'];
              return order.indexOf(a.geometry?.location_type || 'APPROXIMATE') -
                     order.indexOf(b.geometry?.location_type || 'APPROXIMATE');
            });
            const best = ranked[0];
            const lat = best.geometry?.location?.lat?.();
            const lng = best.geometry?.location?.lng?.();
            if (typeof lat === 'number' && typeof lng === 'number') {
              console.log('✅ [GEOCODE] Google JS success:', {
                lat, lng,
                accuracy: best.geometry?.location_type,
                formatted: best.formatted_address,
              });
              setDeliveryLat(lat);
              setDeliveryLng(lng);
              return;
            }
          } else {
            console.warn('⚠️ [GEOCODE] Google JS returned status:', gResult.status);
          }
        } catch (gErr) {
          console.warn('⚠️ [GEOCODE] Google JS Geocoder failed, falling back to Nominatim:', gErr);
        }
      } else {
        console.warn('⚠️ [GEOCODE] Google Maps JS not ready — using Nominatim fallback');
      }

      // FALLBACK: Use Nominatim structured query (OSM)
      const queries: Array<{params: string, label: string}> = [];

      // 1. MOST PRECISE: Structured query with street address
      if (finalAddress) {
        queries.push({
          params: `street=${encodeURIComponent(finalAddress)}&postalcode=${encodeURIComponent(finalPostcode)}&city=${encodeURIComponent(finalCity)}&country=Germany`,
          label: `Structured: ${finalAddress}, ${finalPostcode} ${finalCity}`,
        });
        // 2. Free-form full address (sometimes works better for unusual formats)
        queries.push({
          params: `q=${encodeURIComponent(`${finalAddress}, ${finalPostcode} ${finalCity}, Germany`)}`,
          label: `Free-form: ${finalAddress}, ${finalPostcode} ${finalCity}`,
        });
        // 3. Street + city without postcode (in case postcode confuses Nominatim)
        queries.push({
          params: `street=${encodeURIComponent(finalAddress)}&city=${encodeURIComponent(finalCity)}&country=Germany`,
          label: `Street+City: ${finalAddress}, ${finalCity}`,
        });
      }

      // 4. FALLBACK: Postcode + City only (least precise - city center)
      queries.push({
        params: `q=${encodeURIComponent(`${finalPostcode} ${finalCity}, Germany`)}`,
        label: `Fallback: ${finalPostcode} ${finalCity}`,
      });

      let fallbackLat: number | undefined;
      let fallbackLon: number | undefined;

      for (let i = 0; i < queries.length; i++) {
        const { params, label } = queries[i];
        console.log(`🔍 [GEOCODE] Query ${i + 1}/${queries.length}: ${label}`);

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?${params}&format=json&limit=1&countrycodes=de&addressdetails=1`,
            {
              headers: {
                'User-Agent': 'SushiPOS/1.0',
              },
            }
          );

          console.log(`📡 [GEOCODE] Response status for query ${i + 1}:`, response.status, response.statusText);

          if (response.ok) {
            const data = await response.json();
            console.log(`✅ [GEOCODE] Response data for query ${i + 1}:`, JSON.stringify(data, null, 2));

            if (data && data.length > 0) {
              const result = data[0];
              const lat = parseFloat(result.lat);
              const lon = parseFloat(result.lon);

              // Check result type - prefer building/house/road level over city/postcode level
              const isStreetLevel = ['house', 'building', 'residential', 'road', 'street', 'address'].some(
                t => (result.type || '').includes(t) || (result.class || '').includes(t)
              );

              console.log(`📍 [GEOCODE] Query ${i + 1}: lat=${lat}, lon=${lon}, type=${result.type}, class=${result.class}, streetLevel=${isStreetLevel}`);

              if (!isNaN(lat) && !isNaN(lon)) {
                // If this is a street-level result, use it immediately
                // If it's city/postcode level but we still have more queries, try next for better precision
                if (isStreetLevel || i === queries.length - 1) {
                  console.log('✅ [GEOCODE] ========== SUCCESS! ==========');
                  console.log(`✅ [GEOCODE] Final coordinates: lat=${lat}, lon=${lon} (${isStreetLevel ? 'STREET-LEVEL' : 'AREA-LEVEL'})`);
                  setDeliveryLat(lat);
                  setDeliveryLng(lon);
                  return;
                } else {
                  // Got area-level result, but keep trying for street-level
                  console.log(`📍 [GEOCODE] Got area-level result, trying next query for better precision...`);
                  // Store as fallback
                  fallbackLat = lat;
                  fallbackLon = lon;
                }
              }
            } else {
              console.warn(`⚠️ [GEOCODE] No results from query ${i + 1}, trying next...`);
            }
          } else {
            console.warn(`⚠️ [GEOCODE] Response not OK for query ${i + 1}:`, response.status);
          }
        } catch (fetchError) {
          console.error(`❌ [GEOCODE] Fetch error for query ${i + 1}:`, fetchError);
        }
      }

      // Use fallback coordinates if we got area-level results but no street-level
      if (fallbackLat !== undefined && fallbackLon !== undefined) {
        console.log(`📍 [GEOCODE] Using area-level fallback: lat=${fallbackLat}, lon=${fallbackLon}`);
        setDeliveryLat(fallbackLat);
        setDeliveryLng(fallbackLon);
        return;
      }

      console.warn('⚠️ [GEOCODE] ========== FAILED - No valid coordinates found from any query ==========');
      setDeliveryLat(undefined);
      setDeliveryLng(undefined);
    } catch (error) {
      console.error('❌ [GEOCODE] ========== ERROR ==========');
      console.error('❌ [GEOCODE] Error geocoding address:', error);
      setDeliveryLat(undefined);
      setDeliveryLng(undefined);
    }
  };

  // Handle postcode change - Auto-search and fill if single result
  useEffect(() => {
    if (customerPostcode && customerPostcode.length >= 5) {
      const debounceTimer = setTimeout(() => {
        searchAddress(customerPostcode);
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      setAddressSuggestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPostcode]);

  // Function to reload customers
  const reloadCustomers = useCallback(() => {
    try {
      const customers = customerService.getAllCustomers();
        const customersArray = Array.from(customers.values())
          .map((customer) => {
            // Ensure all fields are preserved and properly formatted
            const customerData = {
              ...customer,
              firstName: customer.firstName || '',
              lastName: customer.lastName || '',
              name: customer.name || '',
              phone: customer.phone || '',
              address: customer.address || '',
              postcode: customer.postcode || '',
              city: customer.city || '',
              // Create display name
              displayName: customer.name || 
                (customer.firstName && customer.lastName ? `${customer.firstName} ${customer.lastName}`.trim() : '') ||
                customer.firstName ||
                customer.lastName ||
                customer.phone ||
                '',
            };
            console.log('📦 [CUSTOMER] Processed customer:', customerData);
            return customerData;
          })
          .filter((customer) => customer && customer.displayName) // Only include valid customers with a name
          .sort((a, b) => {
            // Sort by most recent orders first
            const dateA = a.lastOrderDate || '';
            const dateB = b.lastOrderDate || '';
            return dateB.localeCompare(dateA);
          });
        
      setAllCustomers(customersArray);
      console.log('✅ [CUSTOMER] Loaded all customers:', customersArray.length);
      if (customersArray.length > 0) {
        console.log('📋 [CUSTOMER] Sample customer data:', customersArray[0]);
      }
    } catch (error) {
      console.error('❌ [CUSTOMER] Error loading customers:', error);
    }
  }, [customerService]);

  // Load all customers when dialog opens
  useEffect(() => {
    if (open) {
      reloadCustomers();
      // Reset customer state when dialog opens
      setCustomerName('');
      setSelectedCustomer(null);
      setCustomerMenuAnchor(null);
      setCustomerMenuPosition(null);
    }
  }, [open, reloadCustomers]);

  // Search customers when customerName changes - DISABLED: Using List button instead
  // Autocomplete dropdown disabled to avoid UI clutter with many customers
  useEffect(() => {
    if (!open) return;
    
    // Disable autocomplete - always clear suggestions
      setCustomerSuggestions([]);
      setCustomerMenuAnchor(null);
      setCustomerMenuPosition(null);
  }, [open, customerName, allCustomers]);

  // Geocode address when address fields change (for map display)
  // Re-geocode whenever address/postcode/city changes for best precision
  useEffect(() => {
    console.log('🔄 [GEOCODE] Address fields changed:', {
      address: customerAddress,
      postcode: customerPostcode,
      city: customerCity,
      hasCoords: !!(deliveryLat && deliveryLng),
    });

    if (customerPostcode || customerCity) {
      const addressToUse = customerAddress || '';
      const postcodeToUse = customerPostcode || '50354';
      const cityToUse = customerCity || 'Hürth';

      console.log('📍 [GEOCODE] Triggering geocoding with:', { addressToUse, postcodeToUse, cityToUse });

      const debounceTimer = setTimeout(() => {
        geocodeAddress(addressToUse, postcodeToUse, cityToUse);
      }, 800);

      return () => clearTimeout(debounceTimer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAddress, customerPostcode, customerCity]);

  // Save draft to Firestore
  const saveDraftToFirestore = useCallback(async () => {
    if (cart.length === 0) {
      console.log('⚠️ [CHECKOUT DRAFT] Cannot save draft - cart is empty');
      return;
    }
    
    try {
      console.log('💾 [CHECKOUT DRAFT] Preparing to save draft to Firestore...', {
        cart_items_count: cart.length,
        table_number: tableDatabaseNumber || tableNumber, // Use database number if available, otherwise use display number
        order_type: orderType,
        has_customer_data: !!(customerName || customerPhone || customerAddress)
      });
      
      setIsSavingDraft(true);
      
      const _tblNum = tableContext?.number || tableContext?.display_number;
      const dineInLabel = _tblNum ? `Dine-In Tisch ${_tblNum}` : 'Dine-In';
      const finalCustomerName = (customerFirstName && customerLastName)
        ? `${customerFirstName} ${customerLastName}`.trim()
        : customerName || (orderType === 'delivery' ? 'Laufkunde' : (orderType === 'dine_in' ? dineInLabel : 'Laufkunde'));

      // Determine source
      const source = tableContext ? 'restaurant' : 'dashboard';
      
      const savedDocId = await checkoutDraftService.saveDraft({
        cart_items: cart.map(item => ({
          id: item.id,
          cartId: item.cartId || Date.now(),
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          base_price: item.base_price,
          tax_rate: item.tax_rate,
          category_name: item.category_name,
          image_url: item.image_url,
        })),
        customer_name: finalCustomerName || undefined,
        customer_first_name: customerFirstName || undefined,
        customer_last_name: customerLastName || undefined,
        customer_phone: customerPhone || undefined,
        customer_address: customerAddress || undefined,
        customer_postcode: customerPostcode || undefined,
        customer_city: customerCity || undefined,
        table_number: tableDatabaseNumber || tableNumber || undefined, // Use database number if available
        order_type: orderType,
        payment_method: paymentMethod,
        subtotal: cartTotals.subtotal,
        tax: cartTotals.tax,
        total: cartTotals.total,
        status: 'draft',
        source: source,
        table_context: tableContext || undefined,
        created_by: 'POS System',
      }, currentDraftId || undefined);
      
      // Get the saved draft to retrieve draft_id
      if (savedDocId) {
        try {
          // Get draft by document ID (the ID returned from saveDraft)
          const { doc, getDoc } = await import('../services/firestoreCompat');
          const { db } = await import('../services/firebaseConfig');
          const draftDoc = await getDoc(doc(db, 'checkout_drafts', savedDocId));
          if (draftDoc.exists()) {
            const draftData = draftDoc.data();
            if (draftData.draft_id) {
              setCurrentDraftId(draftData.draft_id);
            }
          }
        } catch (error) {
          console.error('❌ [CHECKOUT DRAFT] Error getting saved draft:', error);
        }
      }
      
      setDraftSaved(true); // Mark that draft was saved
      console.log('✅ [CHECKOUT DRAFT] Draft saved successfully');
    } catch (error) {
      console.error('❌ [CHECKOUT DRAFT] Error saving draft:', error);
      throw error;
    } finally {
      setIsSavingDraft(false);
    }
  }, [cart, tableNumber, tableDatabaseNumber, orderType, paymentMethod, customerName, customerFirstName, customerLastName, customerPhone, customerAddress, customerPostcode, customerCity, cartTotals, tableContext, currentDraftId, checkoutDraftService]);

  // Reset PayPal error when dialog closes
  useEffect(() => {
    if (!open) {
      setPaypalError(null);
    }
  }, [open]);

  const [validationMissing, setValidationMissing] = useState<string[]>([]);

  const handleCheckout = async () => {
    setValidationMissing([]);

    // Validate required fields for delivery orders
    if (orderType === 'delivery') {
      const missing: string[] = [];
      if (!customerFirstName.trim()) missing.push(t('checkout.first_name'));
      if (!customerLastName.trim()) missing.push(t('checkout.last_name'));
      if (!customerPhone.trim()) missing.push(t('checkout.customer_phone'));
      if (!customerAddress.trim()) missing.push(t('checkout.address'));
      if (!customerPostcode.trim()) missing.push(t('checkout.postcode'));
      if (!customerCity.trim()) missing.push(t('checkout.city'));
      if (missing.length > 0) {
        setValidationMissing(missing);
        return;
      }
      if (deliveryBlocked) {
        return;
      }
    }

    if (useSplitPayment) {
      setSplitDialogOpen(true);
      return;
    }

    setIsProcessing(true);
    setPaypalError(null);
    
    try {
      // Use full name if available, otherwise use customer_name, otherwise existing table customer, otherwise default
      const finalCustomerName = (customerFirstName && customerLastName)
        ? `${customerFirstName} ${customerLastName}`.trim()
        : customerName || tableExistingCustomerName || tableContext?.customer_name || (orderType === 'dine_in' ? ((tableContext?.number || tableContext?.display_number) ? `Dine-In Tisch ${tableContext?.number || tableContext?.display_number}` : 'Dine-In') : 'Laufkunde');

      // Calculate total amount
      const totalAmount = cartTotals.total;
      // CRITICAL: Card works like cash (immediate payment via card swipe machine at POS)
      // Only PayPal requires QR code and pending status
      let paymentStatus: 'pending' | 'completed' = (paymentMethod === 'cash' || paymentMethod === 'card') ? 'completed' : 'pending';

      const checkoutData: CheckoutData = {
        customer_name: finalCustomerName,
        customer_first_name: customerFirstName,
        customer_last_name: customerLastName,
        customer_phone: customerPhone,
        customer_address: customerAddress,
        customer_postcode: customerPostcode,
        customer_city: customerCity,
        delivery_latitude: deliveryLat,
        delivery_longitude: deliveryLng,
        table_number: tableDatabaseNumber || tableNumber, // Use database number if available, otherwise use display number
        table_area: (tableContext?.area || selectedTableArea) as 'indoor' | 'outdoor' | undefined, // CRITICAL: Include area from tableContext or visual selector
        order_type: orderType,
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        use_split_payment: false,
        delivery_fee: deliveryFee,
        // Discount (applies to any order type — dine-in, takeaway, delivery)
        ...(discountApplied && discountAmount > 0 ? {
          discount_type: discountType,
          discount_value: parseFloat(discountValue) || 0,
          discount_amount: discountAmount,
          discount_reason: discountReason || (discountType === 'percentage'
            ? `${parseFloat(discountValue) || 0}% Rabatt`
            : `€${(parseFloat(discountValue) || 0).toFixed(2)} Rabatt`),
          total_before_discount: totalBeforeDiscount,
        } : {}),
      };

      // CRITICAL: Log payment method to verify it's being sent correctly
      console.log('💳 [CHECKOUT] Payment method in checkoutData:', {
        paymentMethod,
        paymentStatus,
        checkoutDataPaymentMethod: checkoutData.payment_method,
      });

      // Log order data for verification
      console.log('📋 [CHECKOUT] Order data being created:', {
        customer_name: finalCustomerName,
        order_type: orderType,
        table_number: tableDatabaseNumber || tableNumber, // Use database number if available, otherwise use display number
        payment_method: paymentMethod,
        has_customer_data: !!(finalCustomerName || customerPhone || customerAddress),
      });

      // Complete draft in background — does NOT block order creation
      if (currentDraftId) {
        const _draftId = currentDraftId;
        (async () => {
          try {
            const draft = await checkoutDraftService.getDraftByDraftId(_draftId);
            if (draft && draft.id) await checkoutDraftService.completeDraft(draft.id);
          } catch { /* non-critical */ }
        })();
      }

      // Create order first
      console.log('📋 [CHECKOUT] Calling onCheckout to create order...', {
        paymentMethod,
        isPayPal: paymentMethod === 'paypal',
        isImmediatePayment: paymentMethod === 'cash' || paymentMethod === 'card',
      });
      await onCheckout(checkoutData);
      console.log('✅ [CHECKOUT] Order created successfully, continuing with payment flow...');

      // CRITICAL: Only PayPal requires QR code - Card works like cash (immediate payment via card swipe)
      if (paymentMethod === 'paypal') {
        console.log('💳 [PAYPAL] Payment method is PayPal, creating PayPal order...');
        try {
          // Get the created order ID from the checkout result
          // We need to get the latest order for this customer/table
          const { collection, query, orderBy, limit, getDocs } = await import('../services/firestoreCompat');
          const { db } = await import('../services/firebaseConfig');
          
          // Find the order we just created (most recent order)
          const ordersRef = collection(db, 'orders');
          const q = query(
            ordersRef,
            orderBy('created_at', 'desc'),
            limit(1)
          );
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const createdOrderId = querySnapshot.docs[0].id;
            const createdOrderData = querySnapshot.docs[0].data();
            
            console.log('💳 [PAYPAL] Creating PayPal order for Firebase order:', createdOrderId);
            
            // CRITICAL FIX: Use cart total (totalAmount) for PayPal, not Firebase order total
            // This ensures the PayPal amount matches what the customer sees in the cart
            const paypalOrderAmount = totalAmount;
            
            console.log('💰 [PAYPAL] Amount comparison:', {
              cartTotal: totalAmount,
              orderTotal: createdOrderData.total,
              paypalAmount: paypalOrderAmount,
              match: totalAmount === paypalOrderAmount,
            });

            // Create PayPal order with cart total (what customer sees)
            console.log('💳 [PAYPAL] Calling createOrder service...', {
              amount: paypalOrderAmount,
              orderId: createdOrderId,
              currency: 'EUR',
            });
            
            const paypalResult = await paypalService.createOrder({
              amount: paypalOrderAmount, // Use cart total - what customer sees
              orderId: createdOrderId,
              currency: 'EUR',
              description: `Order ${createdOrderData.order_number || createdOrderId}`,
            });

            console.log('💳 [PAYPAL] createOrder result:', {
              success: paypalResult.success,
              hasApprovalUrl: !!paypalResult.approvalUrl,
              orderId: paypalResult.orderId,
              amount: paypalOrderAmount,
              error: paypalResult.error,
            });

            if (paypalResult.success && paypalResult.approvalUrl) {
              console.log('✅ [PAYPAL] Setting up QR code dialog...', {
                orderId: createdOrderId,
                approvalUrl: paypalResult.approvalUrl.substring(0, 50) + '...',
                amount: paypalOrderAmount,
              });
              
              // CRITICAL: Notify parent component FIRST, before any state updates
              console.log('🔍🔍🔍 [PAYPAL] ========== CALLBACK CHECK START ==========');
              console.log('🔍🔍🔍 [PAYPAL] Line 816-821 EXECUTING NOW!');
              console.log('🔍🔍🔍 [PAYPAL] Checking if callback exists:', { 
                hasCallback: !!onPayPalOrderCreated,
                callbackType: typeof onPayPalOrderCreated,
                isFunction: typeof onPayPalOrderCreated === 'function',
                onPayPalOrderCreated: onPayPalOrderCreated
              });
              if (onPayPalOrderCreated) {
                console.log('📢 [PAYPAL] Notifying parent component to render PayPalQRPayment');
                try {
                  // CRITICAL: Pass original cart items AND order_type so QR receipt can use correct VAT
                  onPayPalOrderCreated(createdOrderId, paypalResult.approvalUrl, paypalOrderAmount, cart, orderType);
                  console.log('✅ [PAYPAL] Callback executed successfully with cart items:', cart.length, 'order_type:', orderType);
                } catch (error) {
                  console.error('❌ [PAYPAL] Error executing callback:', error);
                }
              } else {
                console.warn('⚠️ [PAYPAL] onPayPalOrderCreated callback is NOT provided! QR dialog will not appear in parent.');
              }
              
              // PayPal order amount is passed directly to parent callback
              
              // Update Firebase order with PayPal approval URL and ensure payment_method is correct
              try {
                const { updateDoc, doc } = await import('../services/firestoreCompat');
                const { db } = await import('../services/firebaseConfig');
                await updateDoc(doc(db, 'orders', createdOrderId), {
                  paypal_approval_url: paypalResult.approvalUrl,
                  payment_method: paymentMethod, // Ensure payment method is set correctly
                  updated_at: new Date(),
                });
                console.log('✅ [PAYPAL] Order updated with approval URL and payment method:', paymentMethod);
                console.log('✅ [PAYPAL] Automatic print (PDF + ESC/POS) will be handled by MinimalistDashboard via onPayPalOrderCreated callback');
              } catch (updateError) {
                console.error('❌ [PAYPAL] Failed to update order with approval URL:', updateError);
              }
              
              // Set all state values immediately (no startTransition - we want immediate render)
              console.log('✅ [PAYPAL] QR dialog setup complete - callback executed, parent will show QR dialog');
              // Don't close checkout dialog yet - wait for payment
              setIsProcessing(false);
              return; // Exit early - QR dialog will handle completion
            } else {
              console.error('❌ [PAYPAL] PayPal order creation failed:', paypalResult.error);
              throw new Error(paypalResult.error || 'Failed to create PayPal order');
            }
          } else {
            throw new Error('Order not found after creation');
          }
        } catch (error: any) {
          console.error('❌ [PAYPAL] Error creating PayPal order:', error);
          setPaypalError(error.message || t('checkout.paypal_create_failed'));
          setIsProcessing(false);
          return; // Don't close on error
        }
      }
      
      // If we reach here, payment method is cash or card (PayPal payments return early above)
      // Save customer data and close dialog for cash/card payments (both are immediate)
      console.log(`💵 [CHECKOUT] Payment method is ${paymentMethod}, saving customer data and closing dialog`);
      
      // Save customer data in background — does NOT block dialog close
      const hasValidName = finalCustomerName && finalCustomerName !== 'Laufkunde' && finalCustomerName.trim().length > 0;
      const hasPhone = customerPhone && customerPhone.trim().length > 0;
      const hasAddress = (customerAddress && customerAddress.trim().length > 0) ||
                         (customerPostcode && customerPostcode.trim().length > 0) ||
                         (customerCity && customerCity.trim().length > 0);

      if (hasValidName || hasPhone || hasAddress || customerFirstName || customerLastName) {
        const _customerDataToSave: any = {};
        if (customerFirstName) _customerDataToSave.firstName = customerFirstName.trim();
        if (customerLastName) _customerDataToSave.lastName = customerLastName.trim();
        if (hasValidName) _customerDataToSave.name = finalCustomerName.trim();
        if (customerPhone) _customerDataToSave.phone = customerPhone.trim();
        if (customerAddress) _customerDataToSave.address = customerAddress.trim();
        if (customerPostcode) _customerDataToSave.postcode = customerPostcode.trim();
        if (customerCity) _customerDataToSave.city = customerCity.trim();

        // localStorage save is synchronous and fast — do it immediately
        try { customerService.saveCustomer(_customerDataToSave); } catch { /* ignore */ }

        // Firestore upsert runs in background
        (async () => {
          try {
            const { collection, addDoc, query, where, getDocs, updateDoc, doc, serverTimestamp } = await import('../services/firestoreCompat');
            const { db } = await import('../services/firebaseConfig');
            const customersRef = collection(db, 'customers');

            // Run phone and name lookups in parallel
            const [phoneSnap, nameSnap] = await Promise.all([
              _customerDataToSave.phone
                ? getDocs(query(customersRef, where('phone', '==', _customerDataToSave.phone)))
                : Promise.resolve(null),
              _customerDataToSave.name
                ? getDocs(query(customersRef, where('name', '==', _customerDataToSave.name)))
                : Promise.resolve(null),
            ]);

            const customerDocId = (!phoneSnap?.empty && phoneSnap?.docs[0]?.id) ||
                                  (!nameSnap?.empty && nameSnap?.docs[0]?.id) || null;

            const firestoreData: any = {
              name: _customerDataToSave.name || '',
              firstName: _customerDataToSave.firstName || '',
              lastName: _customerDataToSave.lastName || '',
              phone: _customerDataToSave.phone || '',
              address: _customerDataToSave.address || '',
              postcode: _customerDataToSave.postcode || '',
              city: _customerDataToSave.city || '',
              updated_at: serverTimestamp(),
            };

            if (customerDocId) {
              await updateDoc(doc(db, 'customers', customerDocId), firestoreData);
            } else {
              firestoreData.created_at = serverTimestamp();
              await addDoc(customersRef, firestoreData);
            }
            setTimeout(() => reloadCustomers(), 100);
          } catch { /* non-critical */ }
        })();
      }
      
      // Close checkout dialog (only for cash payments)
      console.log('🚪 [CHECKOUT] Closing dialog for cash payment');
      onClose();
      // Reset form
      setCustomerFirstName('');
      setCustomerLastName('');
      setCustomerName('');
      setCustomerPhone('');
      setCustomerAddress('');
      setCustomerPostcode('');
      setCustomerCity('');
      setTableNumber(null);
      setOrderType('dine_in');
      setPaymentMethod('cash');
      setUseSplitPayment(false);
      setPaypalError(null);
    } catch (error: any) {
      console.error('❌ [CHECKOUT] Error:', error);
      setPaypalError(error.message || t('checkout.payment_processing_failed'));
      // Don't close dialog on error - let user retry
    } finally {
      setIsProcessing(false);
    }
  };

  // Note: Receipt regeneration after payment completion is now handled in MinimalistDashboard
  // since PayPalQRPayment component is rendered there
  // Automatic print (PDF + ESC/POS) is also handled in MinimalistDashboard via onPayPalOrderCreated callback

  // REMOVED: regenerateReceiptWithQRCode function - no longer needed
  // Automatic print is now handled in MinimalistDashboard.tsx via onPayPalOrderCreated callback
  // The QR receipt window for customer viewing is opened in MinimalistDashboard.tsx

  const handleSplitPayment = () => {
    setUseSplitPayment(true);
    setSplitDialogOpen(true);
  };

  // Handle close with confirmation if there's unsaved work
  const handleClose = async () => {
    // Check if there's any data entered (cart items or customer info)
    const hasCartItems = cart.length > 0;
    const hasCustomerData = !!(customerName || customerFirstName || customerLastName || customerPhone || customerAddress || customerPostcode || customerCity);
    const hasTableData = tableNumber !== null;
    
    // If no data at all, close immediately
    if (!hasCartItems && !hasCustomerData && !hasTableData) {
      onClose();
      return;
    }
    
    // If draft was already saved in this session, close without asking again
    if (draftSaved) {
      console.log('✅ [CHECKOUT DRAFT] Draft already saved in this session, closing without confirmation');
      onClose();
      return;
    }
    
    // If coming from restaurant with table context, check for existing draft by table
    if (tableContext && (tableDatabaseNumber || tableNumber)) {
      try {
        const tableNumToCheck = tableDatabaseNumber || tableNumber;
        const existingTableDraft = await checkoutDraftService.getActiveDraftByTable(tableNumToCheck!);
        if (existingTableDraft) {
          // Draft exists for this table - close without asking again
          console.log('✅ [CHECKOUT DRAFT] Draft already exists for table, closing without confirmation');
          setCurrentDraftId(existingTableDraft.draft_id || null);
          setDraftSaved(true);
          onClose();
          return;
        }
      } catch (error) {
        console.error('❌ [CHECKOUT DRAFT] Error checking table draft:', error);
        // Continue to show confirmation if check fails
      }
    }
    
    // If we have a current draft ID, check if it still exists
    if (currentDraftId) {
      try {
        const existingDraft = await checkoutDraftService.getDraftByDraftId(currentDraftId);
        if (existingDraft) {
          // Draft exists - close without asking again
          console.log('✅ [CHECKOUT DRAFT] Draft already exists, closing without confirmation');
          setDraftSaved(true);
          onClose();
          return;
        }
      } catch (error) {
        console.error('❌ [CHECKOUT DRAFT] Error checking existing draft:', error);
        // Continue to show confirmation if check fails
      }
    }
    
    // There's work in progress and no existing draft - show confirmation
    setShowCloseConfirm(true);
  };

  // Confirm close and save as draft
  const handleConfirmClose = async () => {
    try {
      console.log('💾 [CHECKOUT DRAFT] Saving draft before closing dialog...');
      // Save as draft before closing
      await saveDraftToFirestore();
      console.log('✅ [CHECKOUT DRAFT] Draft saved successfully before closing');
    } catch (error) {
      console.error('❌ [CHECKOUT DRAFT] Error saving draft before closing:', error);
      alert(t('checkout.save_draft_error'));
      return; // Don't close if save failed
    }
    setShowCloseConfirm(false);
    onClose();
  };

  // Cancel close - close the confirmation dialog and the main checkout dialog
  const handleCancelClose = () => {
    setShowCloseConfirm(false);
    onClose(); // Close the main checkout dialog
  };

  // Delete draft and close
  const handleDeleteAndClose = async () => {
    try {
      // Try to delete by current draft ID first
      if (currentDraftId) {
        try {
          const draft = await checkoutDraftService.getDraftByDraftId(currentDraftId);
          if (draft && draft.id) {
            await checkoutDraftService.deleteDraft(draft.id);
            console.log('✅ [CHECKOUT DRAFT] Draft deleted by draft_id');
          }
        } catch (error) {
          console.error('❌ [CHECKOUT DRAFT] Error deleting draft by draft_id:', error);
        }
      }
      
      // Also try to delete by table number if available (for restaurant orders)
      if ((tableDatabaseNumber || tableNumber) && tableContext) {
        try {
          const tableNumToCheck = tableDatabaseNumber || tableNumber;
          const tableDraft = await checkoutDraftService.getActiveDraftByTable(tableNumToCheck!);
          if (tableDraft && tableDraft.id) {
            await checkoutDraftService.deleteDraft(tableDraft.id);
            console.log('✅ [CHECKOUT DRAFT] Draft deleted by table number');
          }
        } catch (error) {
          console.error('❌ [CHECKOUT DRAFT] Error deleting draft by table:', error);
        }
      }
    } catch (error) {
      console.error('❌ [CHECKOUT DRAFT] Error in delete operation:', error);
      // Don't block closing even if delete fails
    }
    
    // Reset draft state
    setCurrentDraftId(null);
    setDraftSaved(false);
    setShowCloseConfirm(false);
    onClose();
  };

  if (!open) {
    console.log('⏸️ [CHECKOUT DIALOG] Early return - dialog not open');
    return null;
  }

  // CRITICAL: Log right before return to verify we reach the JSX
  console.log('🎯 [CHECKOUT] About to return JSX - PayPalQRPayment component is in render tree');

  return (
    <>
      {/* Full Screen Modal - Same Style as Full Order Details */}
      <Box
        data-modal-overlay="true"
        onClick={(e) => {
          // Only close if clicking the overlay, not the dialog content
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
        sx={{
          position: 'fixed',
          top: { xs: 56, sm: 0 },
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'center',
          zIndex: 1300,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          pointerEvents: 'auto'
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: '#0A2540',
            borderRadius: { xs: 0, sm: '16px' },
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            maxWidth: { xs: '100%', sm: '880px' },
            width: { xs: '100%', sm: '92%' },
            height: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: '90vh' },
            overflow: 'auto',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header - Professional gradient */}
          <Box sx={{
            background: 'linear-gradient(135deg, #558B2F 0%, #7CB342 100%)',
            p: { xs: 2, sm: 2.5 },
            pt: { xs: 3, sm: 2.5 },
            borderTopLeftRadius: { xs: 0, sm: 16 },
            borderTopRightRadius: { xs: 0, sm: 16 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff', letterSpacing: '-0.3px', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                {t('checkout.title')}
              </Typography>
              {tableContext && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5, fontSize: '0.8rem' }}>
                  {`${t('checkout.table_context_label', { number: String(tableContext.number || tableContext.display_number) })}${tableContext.area ? ` · ${tableContext.area === 'indoor' ? t('restaurant.indoor') : t('restaurant.outdoor')}` : ''} · ${t('checkout.restaurant_order')}`}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleClose} sx={{ color: '#fff', width: { xs: 40, sm: 32 }, height: { xs: 40, sm: 32 }, backgroundColor: 'rgba(255,255,255,0.15)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' } }}>
              <Close sx={{ fontSize: { xs: 22, sm: 20 } }} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: { xs: 2, sm: 3 }, maxHeight: { xs: 'none', sm: '70vh' }, overflowY: 'auto', overflowX: 'visible', flex: 1, position: 'relative', backgroundColor: '#252838' }}>
          <Grid container spacing={3}>
            {/* Left Column - Customer & Order Info */}
            <Grid item xs={12} md={6}>
              {/* DINE-IN EXISTING ORDER: Show simplified read-only customer info */}
              {orderType === 'dine_in' && tableContext && (tableExistingCustomerName || tableContext.customer_name) ? (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid rgba(0, 200, 150, 0.3)',
                    backgroundColor: 'rgba(0, 200, 150, 0.08)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TableRestaurant sx={{ fontSize: 18, color: '#00C896' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#00C896' }}>
                        {t('checkout.adding_to_table') || 'Adding to Table'} {tableContext.number || tableContext.display_number}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#9ca3af', ml: 'auto' }}>
                        {tableExistingCustomerName || tableContext.customer_name}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
              <>

              {/* Customer Data Fields - Only for delivery orders */}
              {orderType === 'delivery' && (
              <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, fontSize: '0.75rem', color: '#558B2F', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                {t('checkout.customer_data')}
              </Typography>

              {/* First Name and Last Name - Side by side */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('checkout.first_name')}
                  value={customerFirstName}
                  onChange={(e) => setCustomerFirstName(e.target.value)}
                  placeholder={t('checkout.first_name_placeholder')}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#252838', borderRadius: '8px', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#94a3b8' }, '&.Mui-focused fieldset': { borderColor: '#558B2F', borderWidth: '2px' } } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={t('checkout.last_name')}
                  value={customerLastName}
                  onChange={(e) => setCustomerLastName(e.target.value)}
                  placeholder={t('checkout.last_name_placeholder')}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#252838', borderRadius: '8px', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#94a3b8' }, '&.Mui-focused fieldset': { borderColor: '#558B2F', borderWidth: '2px' } } }}
                />
              </Box>

              <TextField
                fullWidth
                size="small"
                label={t('checkout.customer_phone')}
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder={t('checkout.phone_placeholder')}
                required
                sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { backgroundColor: '#252838', borderRadius: '8px', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#94a3b8' }, '&.Mui-focused fieldset': { borderColor: '#558B2F', borderWidth: '2px' } } }}
              />

              <TextField
                fullWidth
                size="small"
                label={t('checkout.address')}
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
                placeholder={t('checkout.address_placeholder')}
                required
                sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { backgroundColor: '#252838', borderRadius: '8px', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#94a3b8' }, '&.Mui-focused fieldset': { borderColor: '#558B2F', borderWidth: '2px' } } }}
              />

              {/* Postcode with Address Autocomplete */}
              <Autocomplete
                freeSolo
                value={customerPostcode || null}
                inputValue={customerPostcode || ''}
                onInputChange={(_event, newValue) => {
                  // Extract postcode if user types directly (e.g., "50354" or "50354 Hürth")
                  const postcodeMatch = (newValue || '').match(/\d{5}/);
                  if (postcodeMatch) {
                    setCustomerPostcode(postcodeMatch[0]);
                  } else {
                    setCustomerPostcode(newValue || '');
                  }
                }}
                options={addressSuggestions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  const addr = option as AddressSuggestion;
                  return addr.address || `${addr.street}, ${addr.postcode} ${addr.city}`;
                }}
                loading={loadingAddress}
                onChange={(_event, newValue) => {
                  if (newValue && typeof newValue !== 'string') {
                    const addr = newValue as AddressSuggestion;
                    // Only update if we have a value - preserve existing values
                    if (addr.street || addr.address) {
                      setCustomerAddress(addr.street || addr.address || '');
                    }
                    if (addr.postcode) {
                      setCustomerPostcode(addr.postcode);
                    }
                    if (addr.city) {
                      setCustomerCity(addr.city);
                    }
                    // Set coordinates if available
                    if (addr.lat && addr.lon) {
                      setDeliveryLat(addr.lat);
                      setDeliveryLng(addr.lon);
                    } else {
                      // Geocode the address to get coordinates
                      geocodeAddress(
                        addr.street || addr.address || '',
                        addr.postcode || '',
                        addr.city || ''
                      );
                    }
                  }
                }}
                componentsProps={{
                  popper: {
                    placement: 'bottom-start',
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 4],
                        },
                      },
                      {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                          rootBoundary: 'viewport',
                          tether: false,
                          altAxis: true,
                        },
                      },
                    ],
                    style: {
                      zIndex: 1300,
                    },
                  },
                }}
                ListboxProps={{
                  style: {
                    maxHeight: '300px',
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    value={customerPostcode || ''}
                    label={t('checkout.postcode')}
                    placeholder={t('checkout.postcode_placeholder')}
                    helperText={t('checkout.postcode_helper')}
                    required
                    sx={{
                      mb: 1.5,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838',
                        borderRadius: '8px',
                        '& fieldset': {
                          borderColor: '#3a3d52',
                        },
                        '&:hover fieldset': {
                          borderColor: '#94a3b8',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#558B2F',
                          borderWidth: '2px',
                        },
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAddress ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const addr = option as AddressSuggestion;
                  const { key, ...otherProps } = props;
                  return (
                  <Box component="li" key={key} {...otherProps}>
                    <Typography variant="body2">
                        {addr.street && `${addr.street}, `}
                        {addr.postcode} {addr.city}
                        {addr.state && `, ${addr.state}`}
                    </Typography>
                  </Box>
                  );
                }}
              />
              
              {/* City - Auto-filled from postcode */}
              <TextField
                fullWidth
                size="small"
                value={customerCity}
                label={t('checkout.city')}
                required
                onChange={(e) => {
                  setCustomerCity(e.target.value);
                  // Re-geocode when city changes
                  if (customerAddress && customerPostcode && e.target.value) {
                    geocodeAddress(customerAddress, customerPostcode, e.target.value);
                  }
                }}
                placeholder={t('checkout.city_placeholder')}
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#252838',
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: '#3a3d52',
                    },
                    '&:hover fieldset': {
                      borderColor: '#94a3b8',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#558B2F',
                      borderWidth: '2px',
                    },
                  },
                }}
              />

              {/* Google Maps Delivery Map - Show when we have any address info OR coordinates */}
              {(() => {
                const hasAddressData = (customerPostcode || customerCity || customerAddress) || (deliveryLat && deliveryLng);
                console.log('🗺️ [CHECKOUT] Should show map?', hasAddressData, {
                  hasPostcode: !!customerPostcode,
                  hasCity: !!customerCity,
                  hasAddress: !!customerAddress,
                  hasCoords: !!(deliveryLat && deliveryLng),
                  postcode: customerPostcode,
                  city: customerCity,
                  address: customerAddress,
                  lat: deliveryLat,
                  lng: deliveryLng,
                });
                return hasAddressData;
              })() && (
                <GoogleDeliveryMap
                  deliveryAddress={customerAddress || `${customerPostcode || ''} ${customerCity || ''}`.trim() || 'Hürth'}
                  deliveryPostcode={customerPostcode || '50354'}
                  deliveryCity={customerCity || 'Hürth'}
                  deliveryLat={deliveryLat}
                  deliveryLng={deliveryLng}
                />
              )}
              </Box>
              )}

              {/* Customer Name with Custom Dropdown - Shows all saved customers */}
              <Box sx={{ position: 'relative', width: '100%', mb: 1.5 }}>
                <Typography sx={{ mb: 0.75, fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>
                  {t('checkout.customer_name')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                  <TextField
                    fullWidth
                    value={customerName}
                    onChange={(e) => {
                      setCustomerName(e.target.value);
                      setSelectedCustomer(null);
                    }}
                    placeholder={t('checkout.customer_name_placeholder')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838',
                        borderRadius: '8px',
                        '& fieldset': {
                          borderColor: '#3a3d52',
                        },
                        '&:hover fieldset': {
                          borderColor: '#94a3b8',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#558B2F',
                          borderWidth: '2px',
                        },
                      },
                    }}
                    // Autocomplete disabled - use "List" button instead
                  />
                  <Button
                    onClick={async (e) => {
                      e.stopPropagation();
                      // Open customer list dialog
                      setCustomerListDialogOpen(true);
                      
                      // Load all customers from localStorage - convert Map to Array
                      const customersMap = customerService.getAllCustomers();
                      let customersArray = Array.from(customersMap.values());
                      
                      // Also try to load from Firebase
                      try {
                        const { collection, getDocs } = await import('../services/firestoreCompat');
                        const { db } = await import('../services/firebaseConfig');
                        const customersRef = collection(db, 'customers');
                        const snapshot = await getDocs(customersRef);
                        
                        const firebaseCustomers = snapshot.docs.map(doc => ({
                          id: doc.id,
                          ...doc.data()
                        })) as any[];
                        
                        // Merge Firebase customers with localStorage customers
                        // Use a Map to avoid duplicates (by phone or name)
                        const mergedCustomers = new Map();
                        
                        // Add localStorage customers first
                        customersArray.forEach(customer => {
                          const key = (customer.phone || customer.name || '') as string;
                          if (key) {
                            mergedCustomers.set(key, customer);
                          }
                        });
                        
                        // Add Firebase customers (will overwrite if duplicate)
                        // Map Firebase customer format to match localStorage format
                        firebaseCustomers.forEach((customer: any) => {
                          const key = (customer.phone || customer.full_name || customer.name || '') as string;
                          if (key) {
                            // Normalize Firebase customer data to match expected format
                            const normalizedCustomer = {
                              ...customer,
                              firstName: customer.first_name || customer.firstName,
                              lastName: customer.last_name || customer.lastName,
                              name: customer.full_name || customer.name,
                              phone: customer.phone,
                              address: customer.address,
                              postcode: customer.postcode,
                              city: customer.city,
                              orderCount: customer.total_orders || customer.orderCount || 0
                            };
                            mergedCustomers.set(key, normalizedCustomer);
                          }
                        });
                        
                        customersArray = Array.from(mergedCustomers.values());
                        // Sort by order count (most frequent first) or by name
                        customersArray.sort((a: any, b: any) => {
                          const aOrders = a.orderCount || a.total_orders || 0;
                          const bOrders = b.orderCount || b.total_orders || 0;
                          if (bOrders !== aOrders) {
                            return bOrders - aOrders;
                          }
                          const aName = (a.full_name || a.name || '').toLowerCase();
                          const bName = (b.full_name || b.name || '').toLowerCase();
                          return aName.localeCompare(bName);
                        });
                        console.log('✅ [CUSTOMER LIST] Loaded', customersArray.length, 'customers from Firebase and localStorage');
                      } catch (error) {
                        console.warn('⚠️ [CUSTOMER LIST] Could not load from Firebase, using localStorage only:', error);
                      }
                      
                      setAllCustomers(customersArray);
                    }}
                    startIcon={<Person />}
                    endIcon={<ChevronRight />}
                    sx={{
                      minWidth: '120px',
                      textTransform: 'none',
                      py: 1.5,
                      px: 2,
                      borderRadius: 1,
                      backgroundColor: '#252838',
                      color: '#e0e0e0',
                      border: '1px solid #cbd5e1',
                      '&:hover': {
                        backgroundColor: '#2d3048',
                        borderColor: '#94a3b8',
                      },
                    }}
                  >
                    {t('checkout.list_btn')}
                  </Button>
                </Box>
                
                {/* Customer Dropdown List */}
                {customerMenuAnchor && customerMenuPosition && customerSuggestions.length > 0 && (
                  <Portal>
                    <Box
                      sx={{
                        position: 'fixed',
                        top: customerMenuPosition.top + 4,
                        left: `${customerMenuPosition.left}px`,
                        width: 400,
                        maxHeight: 300,
                        overflowY: 'auto',
                        zIndex: 1300,
                        borderRadius: 1,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#252838',
                        p: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {customerSuggestions.slice(0, 20).map((customer, index) => {
                        const displayName = customer.displayName || 
                          customer.name || 
                          (customer.firstName && customer.lastName ? `${customer.firstName} ${customer.lastName}`.trim() : '') ||
                          customer.phone || 
                          'Unknown';
                        const isSelected = selectedCustomer && (
                          selectedCustomer.name === customer.name ||
                          (selectedCustomer.firstName === customer.firstName && selectedCustomer.lastName === customer.lastName) ||
                          selectedCustomer.phone === customer.phone
                        );
                        
                        return (
                          <Box
                            key={index}
                            onClick={() => {
                              // Fill in ALL customer data from selection
                              console.log('✅ [CUSTOMER] Selected customer (full object):', JSON.stringify(customer, null, 2));
                              
                              // Extract all fields
                              const firstName = (customer.firstName && String(customer.firstName).trim()) || '';
                              const lastName = (customer.lastName && String(customer.lastName).trim()) || '';
                              const phone = (customer.phone && String(customer.phone).trim()) || '';
                              const address = (customer.address && String(customer.address).trim()) || '';
                              const postcode = (customer.postcode && String(customer.postcode).trim()) || '';
                              const city = (customer.city && String(customer.city).trim()) || '';
                              const name = (customer.name && String(customer.name).trim()) || '';
                              
                              // Determine display name
                              let finalDisplayName = '';
                              if (name) {
                                finalDisplayName = name;
                              } else if (firstName && lastName) {
                                finalDisplayName = `${firstName} ${lastName}`.trim();
                              } else if (firstName) {
                                finalDisplayName = firstName;
                              } else if (lastName) {
                                finalDisplayName = lastName;
                              } else if (phone) {
                                finalDisplayName = phone;
                              } else {
                                finalDisplayName = displayName;
                              }
                              
                              // Set all state values
                              setCustomerName(finalDisplayName);
                              setCustomerFirstName(firstName);
                              setCustomerLastName(lastName);
                              setCustomerPhone(phone);
                              setCustomerAddress(address);
                              setCustomerPostcode(postcode);
                              setCustomerCity(city);
                              setSelectedCustomer(customer);
                              
                              console.log('📝 [CUSTOMER] Filled all fields:', {
                                name: finalDisplayName,
                                firstName,
                                lastName,
                                phone,
                                address,
                                postcode,
                                city,
                              });
                              
                              // Close dropdown
                              setCustomerMenuAnchor(null);
                              setCustomerMenuPosition(null);
                              
                              // Trigger geocoding for map if we have postcode OR city
                              setTimeout(() => {
                                if (postcode || city) {
                                  console.log('📍 [CUSTOMER] ✅ Triggering geocoding for map with:', { address, postcode, city });
                                  geocodeAddress(address, postcode, city);
                                }
                              }, 200);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 2,
                              py: 1.5,
                              borderRadius: 1,
                              cursor: 'pointer',
                              backgroundColor: isSelected 
                                ? appTheme.theme.button_background_color || '#ff6b35'
                                : 'transparent',
                              color: isSelected ? '#ffffff' : '#e0e0e0',
                              fontWeight: isSelected ? 600 : 400,
                              '&:hover': {
                                backgroundColor: appTheme.theme.button_background_color || '#ff6b35',
                                color: '#ffffff',
                                '& .MuiSvgIcon-root': {
                                  color: '#ffffff',
                                },
                              },
                            }}
                          >
                            <Person sx={{ fontSize: 20, color: isSelected ? '#ffffff' : '#9ca3af' }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontWeight: isSelected ? 600 : 500, fontSize: '0.95rem' }}>
                                {displayName}
                    </Typography>
                              {customer.phone && (
                                <Typography variant="caption" sx={{ mt: 0.25, color: isSelected ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}>
                                  {customer.phone}
                      </Typography>
                    )}
                              {customer.address && (
                                <Typography variant="caption" sx={{ mt: 0.25, color: isSelected ? 'rgba(255,255,255,0.8)' : 'text.secondary', fontSize: '0.75rem' }}>
                                  {customer.address}
                      </Typography>
                    )}
                            </Box>
                          </Box>
                        );
                      })}
                      {customerSuggestions.length === 0 && customerName.length >= 2 && (
                        <Box sx={{ px: 2, py: 1.5, textAlign: 'center', color: '#9ca3af' }}>
                          <Typography variant="body2">
                            {t('checkout.no_customers_found')}
                          </Typography>
                  </Box>
                )}
                    </Box>
                  </Portal>
                )}
              </Box>

              <Box sx={{ position: 'relative', width: '100%', mb: 1.5 }}>
                <Typography sx={{ mb: 1, fontWeight: 500, color: '#e0e0e0' }}>
                  {t('checkout.order_type')}
                </Typography>
                <Button
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    if (orderTypeMenuAnchor) {
                      // Close menu
                      setOrderTypeMenuAnchor(null);
                      setOrderTypeMenuPosition(null);
                    } else {
                      // Open menu - store button position
                      const rect = e.currentTarget.getBoundingClientRect();
                      setOrderTypeMenuAnchor(e.currentTarget);
                      setOrderTypeMenuPosition({
                        top: rect.top,
                        left: rect.left
                      });
                    }
                  }}
                  startIcon={
                    <Box sx={{ fontSize: '1.2rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                      {orderType === 'dine_in' ? '🍽️' : orderType === 'takeaway' ? '🥡' : '🚚'}
                    </Box>
                  }
                  endIcon={<ChevronRight sx={{ transform: orderTypeMenuAnchor ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
                  sx={{
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    py: 1.5,
                    px: 2,
                    borderRadius: 1,
                  backgroundColor: '#252838',
                    color: '#e0e0e0',
                    border: '1px solid #cbd5e1',
                    '&:hover': {
                      backgroundColor: '#2d3048',
                    borderColor: '#94a3b8',
                  },
                  }}
                >
                  <Typography sx={{ fontWeight: 500 }}>
                    {orderType === 'dine_in' ? t('checkout.dine_in') : orderType === 'takeaway' ? t('checkout.takeaway') : t('checkout.delivery')}
                  </Typography>
                </Button>
                
                {/* Order Type Dropdown List */}
                {orderTypeMenuAnchor && orderTypeMenuPosition && (
                  <Portal>
                    <Box
                      sx={{
                        position: 'fixed',
                        top: orderTypeMenuPosition.top + 48,
                        left: `${orderTypeMenuPosition.left}px`,
                        width: 200,
                        zIndex: 1300,
                        borderRadius: 1,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#252838',
                        p: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Box
                        onClick={() => {
                          console.log('📋 Order type changed to: dine_in');
                          setOrderType('dine_in');
                          setOrderTypeMenuAnchor(null);
                          setOrderTypeMenuPosition(null);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          backgroundColor: orderType === 'dine_in' 
                            ? appTheme.theme.button_background_color || '#ff6b35'
                            : 'transparent',
                          color: orderType === 'dine_in' ? '#ffffff' : '#e0e0e0',
                          fontWeight: orderType === 'dine_in' ? 600 : 400,
                          '&:hover': {
                            backgroundColor: appTheme.theme.button_background_color || '#ff6b35',
                            color: '#ffffff',
                          },
                        }}
                      >
                        <Box sx={{ fontSize: '1.2rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}>🍽️</Box>
                        <Typography sx={{ fontWeight: orderType === 'dine_in' ? 600 : 400 }}>
                          {t('checkout.dine_in')}
                        </Typography>
                      </Box>
                      <Box
                        onClick={() => {
                          console.log('📋 Order type changed to: takeaway');
                          setOrderType('takeaway');
                          setOrderTypeMenuAnchor(null);
                          setOrderTypeMenuPosition(null);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          backgroundColor: orderType === 'takeaway' 
                            ? appTheme.theme.button_background_color || '#ff6b35'
                            : 'transparent',
                          color: orderType === 'takeaway' ? '#ffffff' : '#e0e0e0',
                          fontWeight: orderType === 'takeaway' ? 600 : 400,
                          '&:hover': {
                            backgroundColor: appTheme.theme.button_background_color || '#ff6b35',
                            color: '#ffffff',
                          },
                        }}
                      >
                        <Box sx={{ fontSize: '1.2rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}>🥡</Box>
                        <Typography sx={{ fontWeight: orderType === 'takeaway' ? 600 : 400 }}>
                          {t('checkout.takeaway')}
                        </Typography>
                      </Box>
                      <Box
                        onClick={() => {
                          console.log('📋 Order type changed to: delivery');
                          setOrderType('delivery');
                          setOrderTypeMenuAnchor(null);
                          setOrderTypeMenuPosition(null);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          cursor: 'pointer',
                          backgroundColor: orderType === 'delivery' 
                            ? appTheme.theme.button_background_color || '#ff6b35'
                            : 'transparent',
                          color: orderType === 'delivery' ? '#ffffff' : '#e0e0e0',
                          fontWeight: orderType === 'delivery' ? 600 : 400,
                          '&:hover': {
                            backgroundColor: appTheme.theme.button_background_color || '#ff6b35',
                            color: '#ffffff',
                          },
                        }}
                      >
                        <Box sx={{ fontSize: '1.2rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}>🚚</Box>
                        <Typography sx={{ fontWeight: orderType === 'delivery' ? 600 : 400 }}>
                          {t('checkout.delivery')}
                        </Typography>
                      </Box>
                    </Box>
                  </Portal>
                )}
              </Box>

              {/* Table Selector - Visual grid for Dine-In orders */}
              {orderType === 'dine_in' && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ mb: 1, fontWeight: 500, color: '#e0e0e0', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TableRestaurant sx={{ fontSize: 18, color: '#9ca3af' }} />
                    {t('checkout.table_number')}
                  </Typography>

                  {allTables.length > 0 ? (
                    <>
                      {/* Legend */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#38a169' }} />
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>{t('restaurant.available') || 'Available'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ed8936' }} />
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>{t('restaurant.occupied') || 'Occupied'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#3182ce' }} />
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>{t('restaurant.reserved') || 'Reserved'}</Typography>
                        </Box>
                      </Box>

                      {/* Indoor Tables */}
                      {allTables.filter(t => t.area === 'indoor').length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                            {t('restaurant.indoor') || 'Indoor'}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {allTables.filter(tb => tb.area === 'indoor').map((table) => {
                              const isSelected = tableNumber === table.number && (selectedTableArea === 'indoor' || tableContext?.area === 'indoor');
                              const isOccupied = table.status === 'occupied';
                              const isReserved = table.status === 'reserved';
                              const statusColor = isOccupied ? '#ed8936' : isReserved ? '#3182ce' : '#38a169';
                              return (
                                <Box
                                  key={table.id}
                                  onClick={() => {
                                    if (isOccupied || isReserved) {
                                      setTableWarning({ show: true, table, area: 'indoor' });
                                      return;
                                    }
                                    setTableNumber(table.number);
                                    setTableDatabaseNumber(table.number);
                                    setSelectedTableArea('indoor');
                                  }}
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '10px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    border: isSelected ? `2.5px solid ${statusColor}` : '1.5px solid #3a3d52',
                                    backgroundColor: isSelected ? `${statusColor}18` : '#252838',
                                    boxShadow: isSelected ? `0 0 0 3px ${statusColor}30` : 'none',
                                    transition: 'all 0.15s ease',
                                    position: 'relative',
                                    '&:hover': {
                                      borderColor: statusColor,
                                      backgroundColor: `${statusColor}10`,
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                >
                                  {/* Status dot */}
                                  <Box sx={{
                                    position: 'absolute', top: 3, right: 3,
                                    width: 7, height: 7, borderRadius: '50%',
                                    backgroundColor: statusColor,
                                  }} />
                                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: isSelected ? statusColor : '#333', lineHeight: 1 }}>
                                    {table.number}
                                  </Typography>
                                  {isOccupied && table.customer_name && (
                                    <Typography sx={{ fontSize: '0.5rem', color: '#999', lineHeight: 1, mt: 0.2, maxWidth: 42, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                                      {table.customer_name.split(' ')[0]}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* Outdoor Tables */}
                      {allTables.filter(t => t.area === 'outdoor').length > 0 && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}>
                            {t('restaurant.outdoor') || 'Outdoor'}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {allTables.filter(tb => tb.area === 'outdoor').map((table) => {
                              const isSelected = tableNumber === table.number && (selectedTableArea === 'outdoor' || tableContext?.area === 'outdoor');
                              const isOccupied = table.status === 'occupied';
                              const isReserved = table.status === 'reserved';
                              const statusColor = isOccupied ? '#ed8936' : isReserved ? '#3182ce' : '#38a169';
                              return (
                                <Box
                                  key={table.id}
                                  onClick={() => {
                                    if (isOccupied || isReserved) {
                                      setTableWarning({ show: true, table, area: 'outdoor' });
                                      return;
                                    }
                                    setTableNumber(table.number);
                                    setTableDatabaseNumber(table.number);
                                    setSelectedTableArea('outdoor');
                                  }}
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '10px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    border: isSelected ? `2.5px solid ${statusColor}` : '1.5px solid #3a3d52',
                                    backgroundColor: isSelected ? `${statusColor}18` : '#252838',
                                    boxShadow: isSelected ? `0 0 0 3px ${statusColor}30` : 'none',
                                    transition: 'all 0.15s ease',
                                    position: 'relative',
                                    '&:hover': {
                                      borderColor: statusColor,
                                      backgroundColor: `${statusColor}10`,
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                >
                                  <Box sx={{
                                    position: 'absolute', top: 3, right: 3,
                                    width: 7, height: 7, borderRadius: '50%',
                                    backgroundColor: statusColor,
                                  }} />
                                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: isSelected ? statusColor : '#333', lineHeight: 1 }}>
                                    {table.number}
                                  </Typography>
                                  {isOccupied && table.customer_name && (
                                    <Typography sx={{ fontSize: '0.5rem', color: '#999', lineHeight: 1, mt: 0.2, maxWidth: 42, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                                      {table.customer_name.split(' ')[0]}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* Selected table info */}
                      {tableNumber && (
                        <Box sx={{
                          mt: 1.5, p: 1.5, borderRadius: 1.5,
                          backgroundColor: 'rgba(0, 200, 150, 0.1)', border: '1px solid rgba(0, 200, 150, 0.3)',
                          display: 'flex', alignItems: 'center', gap: 1,
                        }}>
                          <TableRestaurant sx={{ fontSize: 18, color: '#00C896' }} />
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#00C896' }}>
                            {t('checkout.table_context_label', { number: String(tableNumber) }) || `Table ${tableNumber}`}
                            {selectedTableArea && ` (${selectedTableArea === 'indoor' ? t('restaurant.indoor') : t('restaurant.outdoor')})`}
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    /* Fallback: plain number input if tables haven't loaded */
                    <TextField
                      fullWidth
                      type="number"
                      value={tableNumber || ''}
                      onChange={(e) => {
                        const newValue = e.target.value ? parseInt(e.target.value) : null;
                        setTableNumber(newValue);
                        if (newValue !== (tableContext?.number || tableContext?.display_number)) {
                          setTableDatabaseNumber(null);
                        }
                      }}
                      placeholder={t('checkout.table_number_helper')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: '#3a3d52' },
                          '&:hover fieldset': { borderColor: '#000' },
                          '&.Mui-focused fieldset': { borderColor: '#000', borderWidth: '2px' },
                        },
                      }}
                      InputProps={{
                        startAdornment: <TableRestaurant sx={{ mr: 1, color: '#9ca3af' }} />,
                      }}
                    />
                  )}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                {t('checkout.payment_method')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Cash Option */}
                <Box
                  onClick={() => {
                    console.log('💳 Payment method changed to: cash');
                      setPaymentMethod('cash');
                    setPaypalError(null);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    border: paymentMethod === 'cash' ? '2px solid #4CAF50' : '2px solid #3a3d52',
                    backgroundColor: paymentMethod === 'cash' ? 'rgba(76, 175, 80, 0.1)' : '#252838',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    '&:hover': {
                      borderColor: paymentMethod === 'cash' ? '#4CAF50' : '#4CAF50',
                      backgroundColor: paymentMethod === 'cash' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.05)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: paymentMethod === 'cash' ? '2px solid #4CAF50' : '2px solid #bdbdbd',
                      backgroundColor: paymentMethod === 'cash' ? '#4CAF50' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {paymentMethod === 'cash' && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: '#252838',
                        }}
                      />
                    )}
                      </Box>
                  <AttachMoney sx={{ color: paymentMethod === 'cash' ? '#4CAF50' : '#757575', fontSize: 28 }} />
                  <Typography
                    sx={{ 
                      flex: 1,
                      fontWeight: paymentMethod === 'cash' ? 600 : 500,
                      color: paymentMethod === 'cash' ? '#2e7d32' : '#212529',
                      fontSize: '1rem',
                    }}
                  >
                    {t('checkout.cash')}
                  </Typography>
                  {paymentMethod === 'cash' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: '#4CAF50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Check sx={{ fontSize: 14, color: '#ffffff' }} />
                    </Box>
                  )}
                </Box>

                {/* Card Option */}
                <Box
                  onClick={() => {
                    console.log('💳 Payment method changed to: card');
                    setPaymentMethod('card');
                    setPaypalError(null);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    border: paymentMethod === 'card' ? '2px solid #2196F3' : '2px solid #3a3d52',
                    backgroundColor: paymentMethod === 'card' ? 'rgba(33, 150, 243, 0.1)' : '#252838',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    '&:hover': {
                      borderColor: paymentMethod === 'card' ? '#2196F3' : '#2196F3',
                      backgroundColor: paymentMethod === 'card' ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.05)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(33, 150, 243, 0.2)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: paymentMethod === 'card' ? '2px solid #2196F3' : '2px solid #bdbdbd',
                      backgroundColor: paymentMethod === 'card' ? '#2196F3' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {paymentMethod === 'card' && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: '#252838',
                        }}
                      />
                    )}
                  </Box>
                  <CreditCard sx={{ color: paymentMethod === 'card' ? '#2196F3' : '#757575', fontSize: 28 }} />
                  <Typography
                    sx={{
                      flex: 1,
                      fontWeight: paymentMethod === 'card' ? 600 : 500,
                      color: paymentMethod === 'card' ? '#1565C0' : '#212529',
                      fontSize: '1rem',
                    }}
                  >
                    {t('checkout.card')}
                  </Typography>
                  {paymentMethod === 'card' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: '#2196F3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Check sx={{ fontSize: 14, color: '#ffffff' }} />
                      </Box>
                  )}
                </Box>

                {/* PayPal Option */}
                <Box
                  onClick={() => {
                    console.log('💳 Payment method changed to: paypal');
                    setPaymentMethod('paypal');
                    setPaypalError(null);
                  }}
                    sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    border: paymentMethod === 'paypal' ? '2px solid #FF9800' : '2px solid #3a3d52',
                    backgroundColor: paymentMethod === 'paypal' ? 'rgba(255, 152, 0, 0.12)' : '#252838',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    '&:hover': {
                      borderColor: paymentMethod === 'paypal' ? '#FF9800' : '#FF9800',
                      backgroundColor: paymentMethod === 'paypal' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.05)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(255, 152, 0, 0.2)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: paymentMethod === 'paypal' ? '2px solid #FF9800' : '2px solid #bdbdbd',
                      backgroundColor: paymentMethod === 'paypal' ? '#FF9800' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {paymentMethod === 'paypal' && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: '#252838',
                        }}
                      />
                    )}
                  </Box>
                  <AccountBalance sx={{ color: paymentMethod === 'paypal' ? '#FF9800' : '#757575', fontSize: 28 }} />
                  <Typography
                    sx={{
                      flex: 1,
                      fontWeight: paymentMethod === 'paypal' ? 600 : 500,
                      color: paymentMethod === 'paypal' ? '#E65100' : '#212529',
                      fontSize: '1rem',
                    }}
                  >
                    {t('checkout.paypal')}
                  </Typography>
                  {paymentMethod === 'paypal' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: '#FF9800',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Check sx={{ fontSize: 14, color: '#ffffff' }} />
                      </Box>
                  )}
                </Box>
              </Box>

              {/* PayPal Payment Info - Only show for PayPal (Card works like cash) */}
              {paymentMethod === 'paypal' && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 152, 0, 0.12)', borderRadius: 1, border: '1px solid #FF9800' }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <QrCode2 sx={{ color: '#FF9800' }} />
                    {t('checkout.paypal_qr_info')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('checkout.paypal_qr_desc')}
                  </Typography>
                </Box>
              )}

              {/* PayPal Error Display */}
              {paypalError && (
                <Box
                    sx={{ 
                    p: 2,
                    mt: 2,
                    backgroundColor: 'rgba(244, 67, 54, 0.12)',
                    border: '1px solid #f44336',
                      borderRadius: 1, 
                  }}
                >
                  <Typography color="error" variant="body2">
                    {paypalError}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Button
                fullWidth
                variant="outlined"
                startIcon={<CallSplit />}
                onClick={handleSplitPayment}
                sx={{
                  borderColor: '#FF9800',
                  color: '#FF9800',
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#F57C00',
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  },
                }}
              >
                {t('checkout.split_payment')}
              </Button>
              </>
              )}
            </Grid>

            {/* Right Column - Order Summary */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                {t('checkout.order_summary')}
              </Typography>

              <Box sx={{
                border: '1px solid #2a2d3e',
                borderRadius: 2.5,
                p: 1.5,
                mb: 1.5,
                maxHeight: 250,
                overflow: 'auto',
                backgroundColor: '#1e2139'
              }}>
                {cart.map((item) => {
                  // Calculate base price and add-ons
                  const basePrice = item.base_price || item.price || 0;
                  const baseTotal = basePrice * item.quantity;
                  const itemTotal = (item.price * item.quantity);
                  
                  return (
                    <Box key={item.cartId} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #2a2d3e' }}>
                      {/* Main Item Line */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.quantity}x @ €{basePrice.toFixed(2)}
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          €{baseTotal.toFixed(2)}
                        </Typography>
                      </Box>
                      
                      {/* Add-ons */}
                      {item.addons && item.addons.length > 0 && (
                        <Box sx={{ pl: 2, mt: 0.5 }}>
                          {item.addons.map((addon: any, idx: number) => {
                            const qty = addon.quantity || 1;
                            const addonName = language === 'ar' ? (addon.name_de || addon.name) : addon.name;
                            const addonTotal = (addon.price || 0) * qty;
                            return (
                              <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                  + {qty > 1 ? `${qty}x ` : ''}{addonName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1976D2' }}>
                                  +€{addonTotal.toFixed(2)}
                                </Typography>
                              </Box>
                            );
                          })}
                          
                          {/* Item Total */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 0.5, borderTop: '1px solid #3a3d52' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                              = {t('checkout.total')}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                              €{itemTotal.toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">{t('checkout.subtotal')}</Typography>
                <Typography>€{subtotal.toFixed(2)}</Typography>
              </Box>
              {cartTotals.tax7 > 0 && (
                <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>{t('checkout.tax_7')}</Typography>
                  <Typography sx={{ fontSize: '0.875rem' }}>€{cartTotals.tax7.toFixed(2)}</Typography>
                </Box>
              )}
              {cartTotals.tax19 > 0 && (
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>{t('checkout.tax_19')}</Typography>
                  <Typography sx={{ fontSize: '0.875rem' }}>€{cartTotals.tax19.toFixed(2)}</Typography>
                </Box>
              )}
              {cartTotals.tax7 === 0 && cartTotals.tax19 === 0 && (
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">{t('checkout.tax')}</Typography>
                  <Typography>€{taxAmount.toFixed(2)}</Typography>
                </Box>
              )}
              {orderType === 'delivery' && (
                <>
                  <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">
                      {deliveryQuote.isFree ? '🎉 Delivery (FREE)' : 'Delivery Fee'}
                    </Typography>
                    <Typography sx={{
                      color: deliveryQuote.isFree ? '#7CB342' : 'text.primary',
                      fontWeight: deliveryQuote.isFree ? 700 : 400,
                    }}>
                      {deliveryQuote.isFree ? 'FREE' : `€${deliveryFee.toFixed(2)}`}
                    </Typography>
                  </Box>
                  {zoneLoading && (
                    <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(100,116,139,0.15)' }}>
                      <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>Checking delivery zone…</Typography>
                    </Box>
                  )}
                  {!zoneLoading && customerPostcode.trim().length >= 5 && !deliveryZone && (
                    <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' }}>
                      <Typography sx={{ fontSize: '0.85rem', color: '#fca5a5', fontWeight: 600 }}>
                        ❌ We don't deliver to postal code {customerPostcode.trim()} yet.
                      </Typography>
                    </Box>
                  )}
                  {!zoneLoading && deliveryZone && !deliveryZone.available && (
                    <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' }}>
                      <Typography sx={{ fontSize: '0.85rem', color: '#fca5a5', fontWeight: 600 }}>
                        ❌ Delivery to {deliveryZone.city || customerPostcode} is currently disabled.
                      </Typography>
                    </Box>
                  )}
                  {!zoneLoading && deliveryZone && deliveryZone.available && deliveryQuote.reason === 'below_min' && !isAddingToExistingOrder && (
                    <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(245,158,11,0.18)', border: '1px solid rgba(245,158,11,0.5)' }}>
                      <Typography sx={{ fontSize: '0.85rem', color: '#fcd34d', fontWeight: 600 }}>
                        ⚠️ Minimum order €{deliveryZone.min_order.toFixed(2)} — add €{deliveryQuote.amountBelowMin.toFixed(2)} more to enable delivery.
                      </Typography>
                    </Box>
                  )}
                  {!zoneLoading && deliveryQuote.allowed && !deliveryQuote.isFree && deliveryQuote.amountToFreeDelivery > 0 && (
                    <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.4)' }}>
                      <Typography sx={{ fontSize: '0.85rem', color: '#86efac', fontWeight: 600 }}>
                        💡 Add €{deliveryQuote.amountToFreeDelivery.toFixed(2)} more for FREE delivery.
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              {/* Discount section — works for dine-in, takeaway, delivery */}
              <Box sx={{
                mt: 1.5, mb: 1, p: 1.5, borderRadius: 2,
                backgroundColor: discountApplied ? 'rgba(0, 200, 150,0.08)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${discountApplied ? 'rgba(0, 200, 150,0.35)' : '#2a2d3e'}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#00C896', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    🏷️ Discount / Rabatt
                  </Typography>
                  {discountApplied && (
                    <Button
                      size="small"
                      onClick={() => {
                        setDiscountApplied(false);
                        setDiscountValue('');
                        setDiscountReason('');
                      }}
                      sx={{ fontSize: '0.7rem', color: '#F44336', minWidth: 'auto', py: 0.25, px: 1 }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>

                {!discountApplied ? (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        variant={discountType === 'percentage' ? 'contained' : 'outlined'}
                        onClick={() => setDiscountType('percentage')}
                        sx={{
                          minWidth: 36, px: 1, py: 0.5, fontSize: '0.75rem',
                          backgroundColor: discountType === 'percentage' ? '#00C896' : 'transparent',
                          borderColor: '#00C896',
                          color: discountType === 'percentage' ? '#1a1d2e' : '#00C896',
                          '&:hover': {
                            backgroundColor: discountType === 'percentage' ? '#7bc912' : 'rgba(0, 200, 150,0.1)',
                            borderColor: '#00C896',
                          },
                        }}
                      >
                        %
                      </Button>
                      <Button
                        size="small"
                        variant={discountType === 'fixed' ? 'contained' : 'outlined'}
                        onClick={() => setDiscountType('fixed')}
                        sx={{
                          minWidth: 36, px: 1, py: 0.5, fontSize: '0.75rem',
                          backgroundColor: discountType === 'fixed' ? '#00C896' : 'transparent',
                          borderColor: '#00C896',
                          color: discountType === 'fixed' ? '#1a1d2e' : '#00C896',
                          '&:hover': {
                            backgroundColor: discountType === 'fixed' ? '#7bc912' : 'rgba(0, 200, 150,0.1)',
                            borderColor: '#00C896',
                          },
                        }}
                      >
                        €
                      </Button>
                    </Box>
                    <TextField
                      size="small"
                      type="number"
                      placeholder={discountType === 'percentage' ? '10' : '5.00'}
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      inputProps={{ min: 0, step: discountType === 'percentage' ? 1 : 0.5, max: discountType === 'percentage' ? 100 : undefined }}
                      sx={{
                        width: 90,
                        '& .MuiOutlinedInput-root': { height: 32, fontSize: '0.85rem' },
                      }}
                    />
                    <TextField
                      size="small"
                      placeholder="Reason (optional)"
                      value={discountReason}
                      onChange={(e) => setDiscountReason(e.target.value)}
                      sx={{
                        flex: 1, minWidth: 100,
                        '& .MuiOutlinedInput-root': { height: 32, fontSize: '0.85rem' },
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!discountValue || parseFloat(discountValue) <= 0}
                      onClick={() => setDiscountApplied(true)}
                      sx={{
                        backgroundColor: '#00C896',
                        color: '#1a1d2e',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        '&:hover': { backgroundColor: '#7bc912' },
                        '&.Mui-disabled': { backgroundColor: '#3a3d52', color: '#6b7280' },
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#00C896' }}>
                      {discountType === 'percentage'
                        ? `${parseFloat(discountValue) || 0}% off`
                        : `€${(parseFloat(discountValue) || 0).toFixed(2)} off`}
                      {discountReason ? ` — ${discountReason}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#00C896' }}>
                      −€{discountAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {discountApplied && discountAmount > 0 && (
                <>
                  {/* Clear Zwischensumme line — sums up Subtotal + VAT + Delivery */}
                  <Box sx={{
                    mt: 1, mb: 0.5, pt: 1,
                    borderTop: '1px dashed #3a3d52',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#cfd2d9' }}>
                      Zwischensumme / Subtotal
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#cfd2d9' }}>
                      €{totalBeforeDiscount.toFixed(2)}
                    </Typography>
                  </Box>
                  {/* Discount line — clearly negative, bright green */}
                  <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#00C896' }}>
                      {discountType === 'percentage'
                        ? `Rabatt (${parseFloat(discountValue) || 0}%)${discountReason ? ' — ' + discountReason : ''}`
                        : `Rabatt${discountReason ? ' — ' + discountReason : ''}`}
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#00C896' }}>
                      −€{discountAmount.toFixed(2)}
                    </Typography>
                  </Box>
                </>
              )}

              <Box sx={{
                mt: 2, p: 2, borderRadius: 2.5,
                background: 'linear-gradient(135deg, #558B2F 0%, #7CB342 100%)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <Typography sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.8)', fontSize: '1rem' }}>
                  {discountApplied && discountAmount > 0 ? 'Gesamt / Total' : t('checkout.total')}
                </Typography>
                <Typography sx={{ fontWeight: 800, color: '#ffffff', fontSize: '1.4rem', letterSpacing: '-0.5px' }}>
                  €{totalWithFee.toFixed(2)}
                </Typography>
              </Box>

              {discountApplied && discountAmount > 0 && (
                <Typography sx={{ mt: 0.5, fontSize: '0.72rem', color: '#6b7280', textAlign: 'right', fontStyle: 'italic' }}>
                  Subtotal €{totalBeforeDiscount.toFixed(2)} − Rabatt €{discountAmount.toFixed(2)} = €{totalWithFee.toFixed(2)}
                </Typography>
              )}
            </Grid>
          </Grid>
          </Box>

          {/* Footer */}
          <Box sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2.5,
            backgroundColor: '#1e2139',
            borderTop: '1px solid #2a2d3e',
            borderBottomLeftRadius: { xs: 0, sm: 16 },
            borderBottomRightRadius: { xs: 0, sm: 16 },
            flexShrink: 0,
          }}>
            <Button
              onClick={handleClose}
              variant="text"
              sx={{ color: '#94a3b8', fontWeight: 500, textTransform: 'none', fontSize: '0.95rem', '&:hover': { color: '#64748b', backgroundColor: 'transparent' } }}
            >
              {t('checkout.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleCheckout}
              disabled={isProcessing || cart.length === 0 || deliveryBlocked}
              sx={{
                background: 'linear-gradient(135deg, #558B2F 0%, #7CB342 100%)',
                color: '#fff',
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1rem',
                borderRadius: '12px',
                textTransform: 'none',
                letterSpacing: '0.3px',
                boxShadow: '0 4px 14px rgba(85, 139, 47, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #689F38 0%, #8BC34A 100%)',
                  boxShadow: '0 6px 20px rgba(85, 139, 47, 0.4)',
                },
                '&:disabled': {
                  background: '#e2e8f0',
                  color: '#94a3b8',
                  boxShadow: 'none',
                },
              }}
            >
              {isProcessing
                ? t('checkout.processing')
                : deliveryBlocked && deliveryQuote.reason === 'below_min'
                  ? `Minimum €${deliveryQuote.zone?.min_order.toFixed(2)} required`
                  : deliveryBlocked
                    ? 'Delivery not available'
                    : `${t('checkout.complete_order')} · €${totalWithFee.toFixed(2)}`}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Validation Missing Fields Popup */}
      {validationMissing.length > 0 && (
        <Box
          onClick={() => setValidationMissing([])}
          sx={{
            position: 'fixed', top: { xs: 56, sm: 0 }, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1400, backgroundColor: 'rgba(0,0,0,0.5)',
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#252838', borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              maxWidth: '400px', width: '90%', overflow: 'hidden',
            }}
          >
            <Box sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>
                !
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', fontSize: '1.1rem' }}>
                {t('checkout.required_fields_missing')}
              </Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 2, fontSize: '0.9rem' }}>
                {t('checkout.please_fill_required')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {validationMissing.map((field, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: '8px', backgroundColor: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#fecaca' }}>{field}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ p: 2, borderTop: '1px solid #3a3d52', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setValidationMissing([])}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 4, py: 1,
                  '&:hover': { background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' },
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Table Occupied/Reserved Warning Popup */}
      {tableWarning?.show && (
        <Box
          onClick={() => setTableWarning(null)}
          sx={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1400,
            backgroundColor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(3px)',
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#252838',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              maxWidth: 400,
              width: '90%',
              overflow: 'hidden',
            }}
          >
            {/* Warning header */}
            <Box sx={{
              background: tableWarning.table.status === 'occupied'
                ? 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)'
                : 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)',
              p: 2.5,
              display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '1.5rem' }}>
                  {tableWarning.table.status === 'occupied' ? '🍽️' : '📋'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '1.05rem' }}>
                  {t('restaurant.table') || 'Table'} {tableWarning.table.number} — {tableWarning.table.status === 'occupied'
                    ? (t('restaurant.occupied') || 'Occupied')
                    : (t('restaurant.reserved') || 'Reserved')}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
                  {tableWarning.area === 'indoor' ? (t('restaurant.indoor') || 'Indoor') : (t('restaurant.outdoor') || 'Outdoor')}
                </Typography>
              </Box>
            </Box>

            {/* Warning body */}
            <Box sx={{ p: 2.5 }}>
              {tableWarning.table.status === 'occupied' ? (
                <>
                  {tableWarning.table.customer_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: 1.5, backgroundColor: 'rgba(255, 241, 118, 0.1)', borderRadius: '10px', border: '1px solid #ffe082' }}>
                      <Typography sx={{ fontSize: '1rem' }}>👤</Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#e65100' }}>
                        {tableWarning.table.customer_name}
                      </Typography>
                    </Box>
                  )}
                  <Typography sx={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.6 }}>
                    {t('checkout.table_occupied_message') || 'This table has an active order. Please complete or close the existing order first, or choose a different table.'}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.6 }}>
                  {t('checkout.table_reserved_message') || 'This table is reserved. Please choose an available table or cancel the reservation first.'}
                </Typography>
              )}
            </Box>

            {/* Warning actions */}
            <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                onClick={() => setTableWarning(null)}
                sx={{
                  py: 1.3,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: '#fff',
                  background: tableWarning.table.status === 'occupied'
                    ? 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)'
                    : 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)',
                  '&:hover': {
                    background: tableWarning.table.status === 'occupied'
                      ? 'linear-gradient(135deg, #bf360c 0%, #fb8c00 100%)'
                      : 'linear-gradient(135deg, #0d47a1 0%, #2196f3 100%)',
                  },
                }}
              >
                {t('checkout.choose_another_table') || 'Choose Another Table'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Customer List Dialog */}
      {customerListDialogOpen && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setCustomerListDialogOpen(false);
            }
          }}
          sx={{
            position: 'fixed',
            top: { xs: 56, sm: 0 },
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'center',
            zIndex: 1400,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto'
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#252838',
              borderRadius: { xs: 0, sm: 3 },
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: { xs: 'none', sm: '1px solid #e2e8f0' },
              maxWidth: { xs: '100%', sm: '700px' },
              width: { xs: '100%', sm: '90%' },
              maxHeight: { xs: '100%', sm: '80vh' },
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('checkout.customer_list')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  startIcon={<Add />}
                  onClick={() => setAddCustomerDialogOpen(true)}
                  size="small"
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    borderColor: '#94a3b8',
                    color: '#1e293b',
                    '&:hover': {
                      borderColor: '#94a3b8',
                      backgroundColor: '#2d3048',
                    },
                  }}
                >
                  {t('checkout.add_customer')}
                </Button>
              <IconButton onClick={() => setCustomerListDialogOpen(false)} size="small" sx={{ color: '#9ca3af' }}>
                <Close />
              </IconButton>
              </Box>
            </Box>

            {/* Customer List */}
            <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
              {allCustomers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('checkout.no_customers_found')}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {allCustomers.map((customer, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        border: '1px solid #3a3d52',
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 2,
                        '&:hover': {
                          backgroundColor: '#2d3048',
                          borderColor: '#94a3b8',
                        },
                      }}
                    >
                      <Box
                      onClick={() => {
                        // Fill form with selected customer data
                        // Handle both Firebase customer format and localStorage format
                        const firstName = customer.first_name || customer.firstName || '';
                        const lastName = customer.last_name || customer.lastName || '';
                        const fullName = customer.full_name || customer.name || '';
                        const phone = customer.phone || '';
                        const address = customer.address || '';
                        const postcode = customer.postcode || '';
                        const city = customer.city || '';
                        
                        // Set first name and last name
                        if (firstName) {
                          setCustomerFirstName(firstName);
                        }
                        if (lastName) {
                          setCustomerLastName(lastName);
                        }
                        
                        // Set full name if available (for backward compatibility)
                        if (fullName && !firstName && !lastName) {
                          setCustomerName(fullName);
                        } else if (fullName) {
                          setCustomerName(fullName);
                        }
                        
                        // Set phone
                        if (phone) {
                          setCustomerPhone(phone);
                        }
                        
                        // Set address fields
                        if (address) {
                          setCustomerAddress(address);
                        }
                        if (postcode) {
                          setCustomerPostcode(postcode);
                        }
                        if (city) {
                          setCustomerCity(city);
                        }
                        
                        // Store selected customer for reference
                        setSelectedCustomer({
                          ...customer,
                          firstName: firstName,
                          lastName: lastName,
                          name: fullName,
                          phone: phone,
                          address: address,
                          postcode: postcode,
                          city: city
                        });
                        
                        setCustomerListDialogOpen(false);
                        console.log('✅ [CUSTOMER LIST] Selected customer and filled form:', {
                          firstName,
                          lastName,
                          fullName,
                          phone,
                          address,
                          postcode,
                          city
                        });
                      }}
                      sx={{
                          flex: 1,
                        cursor: 'pointer',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {customer.full_name || customer.name || 
                             `${customer.first_name || customer.firstName || ''} ${customer.last_name || customer.lastName || ''}`.trim() || 
                             'Unnamed Customer'}
                          </Typography>
                          {(customer.phone) && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              📞 {customer.phone}
                            </Typography>
                          )}
                        </Box>
                        {(customer.orderCount || customer.total_orders) && (
                          <Typography variant="caption" color="text.secondary">
                            {customer.orderCount || customer.total_orders || 0} {t('checkout.orders_count')}
                          </Typography>
                        )}
                      </Box>
                      {((customer.address || customer.postcode || customer.city)) && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {customer.address && `${customer.address}, `}
                            {customer.postcode && `${customer.postcode} `}
                            {customer.city}
                          </Typography>
                        </Box>
                      )}
                      </Box>
                      <IconButton
                        onClick={async (e) => {
                          e.stopPropagation();
                          const customerName = customer.full_name || customer.name || 
                            `${customer.first_name || customer.firstName || ''} ${customer.last_name || customer.lastName || ''}`.trim();
                          const customerPhone = customer.phone || '';
                          
                          if (window.confirm(
                            t('checkout.confirm_delete_customer', { name: customerName || customerPhone })
                          )) {
                            // Try to delete from Firebase if it has an ID
                            if (customer.id) {
                              await customerService.deleteCustomerById(customer.id);
                            }
                            
                            // Delete from localStorage
                            const key = customerPhone || customerName || '';
                            if (key) {
                              customerService.deleteCustomer(key);
                            }
                            
                            // Refresh the customer list
                            const customersMap = customerService.getAllCustomers();
                            let customersArray = Array.from(customersMap.values());
                            
                            // Reload from Firebase
                            try {
                              const { collection, getDocs } = await import('../services/firestoreCompat');
                              const { db } = await import('../services/firebaseConfig');
                              const customersRef = collection(db, 'customers');
                              const snapshot = await getDocs(customersRef);
                              
                              const firebaseCustomers = snapshot.docs.map(doc => ({
                                id: doc.id,
                                ...doc.data()
                              })) as any[];
                              
                              const mergedCustomers = new Map();
                              customersArray.forEach(c => {
                                const k = (c.phone || c.name || '') as string;
                                if (k) mergedCustomers.set(k, c);
                              });
                              
                              firebaseCustomers.forEach((c: any) => {
                                const k = (c.phone || c.full_name || c.name || '') as string;
                                if (k) {
                                  mergedCustomers.set(k, {
                                    ...c,
                                    firstName: c.first_name || c.firstName,
                                    lastName: c.last_name || c.lastName,
                                    name: c.full_name || c.name,
                                    orderCount: c.total_orders || c.orderCount || 0
                                  });
                                }
                              });
                              
                              customersArray = Array.from(mergedCustomers.values());
                              customersArray.sort((a: any, b: any) => {
                                const aOrders = a.orderCount || a.total_orders || 0;
                                const bOrders = b.orderCount || b.total_orders || 0;
                                if (bOrders !== aOrders) return bOrders - aOrders;
                                const aName = (a.full_name || a.name || '').toLowerCase();
                                const bName = (b.full_name || b.name || '').toLowerCase();
                                return aName.localeCompare(bName);
                              });
                            } catch (error) {
                              console.warn('⚠️ [CUSTOMER LIST] Could not reload from Firebase:', error);
                            }
                            
                            setAllCustomers(customersArray);
                            console.log('✅ [CUSTOMER LIST] Customer deleted');
                          }
                        }}
                        size="small"
                        sx={{
                          color: '#d32f2f',
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.12)',
                          },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Add Customer Dialog - Full Screen Modal Style like Checkout */}
      {addCustomerDialogOpen && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setAddCustomerDialogOpen(false);
              setNewCustomerFirstName('');
              setNewCustomerLastName('');
              setNewCustomerPhone('');
              setNewCustomerAddress('');
              setNewCustomerPostcode('');
              setNewCustomerCity('');
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1400,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto'
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #e2e8f0',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              pointerEvents: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('checkout.add_new_customer')}
              </Typography>
              <IconButton 
                onClick={() => {
                  setAddCustomerDialogOpen(false);
                  setNewCustomerFirstName('');
                  setNewCustomerLastName('');
                  setNewCustomerPhone('');
                  setNewCustomerAddress('');
                  setNewCustomerPostcode('');
                  setNewCustomerCity('');
                }} 
                size="small" 
                sx={{ color: '#9ca3af' }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto', overflowX: 'visible', flex: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* First Name and Last Name - Side by side */}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    fullWidth
                    label={t('checkout.first_name_label')}
                    value={newCustomerFirstName}
                    onChange={(e) => setNewCustomerFirstName(e.target.value)}
                    placeholder={t('checkout.first_name_placeholder')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838',
                        '& fieldset': {
                          borderColor: '#94a3b8',
                        },
                        '&:hover fieldset': {
                          borderColor: '#94a3b8',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#94a3b8',
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('checkout.last_name_label')}
                    value={newCustomerLastName}
                    onChange={(e) => setNewCustomerLastName(e.target.value)}
                    placeholder={t('checkout.last_name_placeholder')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838',
                        '& fieldset': {
                          borderColor: '#94a3b8',
                        },
                        '&:hover fieldset': {
                          borderColor: '#94a3b8',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#94a3b8',
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Phone Number */}
                <TextField
                  fullWidth
                  label={t('checkout.phone_label')}
                  value={newCustomerPhone}
                  onChange={(e) => setNewCustomerPhone(e.target.value)}
                  placeholder={t('checkout.phone_placeholder')}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#252838',
                      '& fieldset': {
                        borderColor: '#3a3d52',
                      },
                      '&:hover fieldset': {
                        borderColor: '#94a3b8',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#7CB342',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />

                {/* Address */}
                <TextField
                  fullWidth
                  label={t('checkout.address_label')}
                  value={newCustomerAddress}
                  onChange={(e) => setNewCustomerAddress(e.target.value)}
                  placeholder={t('checkout.address_placeholder')}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#252838',
                      '& fieldset': {
                        borderColor: '#3a3d52',
                      },
                      '&:hover fieldset': {
                        borderColor: '#94a3b8',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#7CB342',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />

                {/* Postal Code and City - Side by side */}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    fullWidth
                    label={t('checkout.postcode_label')}
                    value={newCustomerPostcode}
                    onChange={(e) => setNewCustomerPostcode(e.target.value)}
                    placeholder={t('checkout.postcode_placeholder')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838',
                        '& fieldset': {
                          borderColor: '#94a3b8',
                        },
                        '&:hover fieldset': {
                          borderColor: '#94a3b8',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#94a3b8',
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('checkout.city_label')}
                    value={newCustomerCity}
                    onChange={(e) => setNewCustomerCity(e.target.value)}
                    placeholder={t('checkout.city_placeholder')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#252838',
                        '& fieldset': {
                          borderColor: '#94a3b8',
                        },
                        '&:hover fieldset': {
                          borderColor: '#94a3b8',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#94a3b8',
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Footer with Action Buttons */}
            <Box sx={{ p: 2, borderTop: '1px solid #3a3d52', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                onClick={() => {
                  setAddCustomerDialogOpen(false);
                  setNewCustomerFirstName('');
                  setNewCustomerLastName('');
                  setNewCustomerPhone('');
                  setNewCustomerAddress('');
                  setNewCustomerPostcode('');
                  setNewCustomerCity('');
                }}
                sx={{
                  textTransform: 'none',
                  color: '#e0e0e0',
                  borderColor: '#94a3b8',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    backgroundColor: '#2d3048',
                  },
                }}
                variant="outlined"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={async () => {
                  const firstName = newCustomerFirstName.trim();
                  const lastName = newCustomerLastName.trim();
                  const phone = newCustomerPhone.trim();
                  const address = newCustomerAddress.trim();
                  const postcode = newCustomerPostcode.trim();
                  const city = newCustomerCity.trim();
                  
                  if (!firstName && !lastName && !phone) {
                    alert(t('checkout.name_or_phone_required'));
                    return;
                  }
                  
                  // Save customer to localStorage
                  const customerData: any = {};
                  if (firstName) customerData.firstName = firstName;
                  if (lastName) customerData.lastName = lastName;
                  if (firstName && lastName) {
                    customerData.name = `${firstName} ${lastName}`.trim();
                  }
                  if (phone) customerData.phone = phone;
                  if (address) customerData.address = address;
                  if (postcode) customerData.postcode = postcode;
                  if (city) customerData.city = city;
                  
                  customerService.saveCustomer(customerData);
                  
                  // Try to save to Firebase
                  try {
                    const { collection, addDoc } = await import('../services/firestoreCompat');
                    const { db } = await import('../services/firebaseConfig');
                    const customersRef = collection(db, 'customers');
                    await addDoc(customersRef, {
                      first_name: firstName || '',
                      last_name: lastName || '',
                      full_name: firstName && lastName ? `${firstName} ${lastName}`.trim() : firstName || lastName || '',
                      phone: phone || '',
                      address: address || '',
                      postcode: postcode || '',
                      city: city || '',
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                    });
                    console.log('✅ [CUSTOMER LIST] Customer saved to Firebase');
                  } catch (error) {
                    console.error('❌ [CUSTOMER LIST] Error saving to Firebase:', error);
                  }
                  
                  // Refresh customer list
                  const customersMap = customerService.getAllCustomers();
                  let customersArray = Array.from(customersMap.values());
                  
                  // Reload from Firebase
                  try {
                    const { collection, getDocs } = await import('../services/firestoreCompat');
                    const { db } = await import('../services/firebaseConfig');
                    const customersRef = collection(db, 'customers');
                    const snapshot = await getDocs(customersRef);
                    
                    const firebaseCustomers = snapshot.docs.map(doc => ({
                      id: doc.id,
                      ...doc.data()
                    })) as any[];
                    
                    const mergedCustomers = new Map();
                    customersArray.forEach(c => {
                      const k = (c.phone || c.name || '') as string;
                      if (k) mergedCustomers.set(k, c);
                    });
                    
                    firebaseCustomers.forEach((c: any) => {
                      const k = (c.phone || c.full_name || c.name || '') as string;
                      if (k) {
                        mergedCustomers.set(k, {
                          ...c,
                          firstName: c.first_name || c.firstName,
                          lastName: c.last_name || c.lastName,
                          name: c.full_name || c.name,
                          orderCount: c.total_orders || c.orderCount || 0
                        });
                      }
                    });
                    
                    customersArray = Array.from(mergedCustomers.values());
                    customersArray.sort((a: any, b: any) => {
                      const aOrders = a.orderCount || a.total_orders || 0;
                      const bOrders = b.orderCount || b.total_orders || 0;
                      if (bOrders !== aOrders) return bOrders - aOrders;
                      const aName = (a.full_name || a.name || '').toLowerCase();
                      const bName = (b.full_name || b.name || '').toLowerCase();
                      return aName.localeCompare(bName);
                    });
                  } catch (error) {
                    console.warn('⚠️ [CUSTOMER LIST] Could not reload from Firebase:', error);
                  }
                  
                  setAllCustomers(customersArray);
                  
                  // Close dialog and reset form
                  setAddCustomerDialogOpen(false);
                  setNewCustomerFirstName('');
                  setNewCustomerLastName('');
                  setNewCustomerPhone('');
                  setNewCustomerAddress('');
                  setNewCustomerPostcode('');
                  setNewCustomerCity('');
                  
                  console.log('✅ [CUSTOMER LIST] New customer added');
                }}
                variant="contained"
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#d32f2f',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  },
                }}
              >
                {t('common.save')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Split Payment Dialog */}
      {splitDialogOpen && (
        <SplitTableDialog
          open={splitDialogOpen}
          onClose={() => {
            setSplitDialogOpen(false);
            setUseSplitPayment(false);
          }}
          tableId={tableNumber || 0}
          tableNumber={(tableNumber || 0).toString()}
          orderId=""
          orderTotal={total}
          orderItems={cart.map(item => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            unitPrice: item.price,
            totalPrice: item.price * item.quantity,
          }))}
          staffMember="POS System"
        />
      )}

      {/* Close Confirmation Dialog - Full Screen Modal Style like Checkout */}
      {showCloseConfirm && (
        <Box
          data-modal-overlay="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCancelClose();
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto'
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#252838',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              border: '1px solid #e2e8f0',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              pointerEvents: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('checkout.close_checkout')}
              </Typography>
              <IconButton 
                onClick={handleCancelClose} 
                size="small" 
                sx={{ color: '#9ca3af' }}
              >
                <Close />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              <Typography sx={{ color: '#e0e0e0', fontSize: '1rem' }}>
                {t('checkout.unsaved_changes_message')}
              </Typography>
            </Box>

            {/* Footer with Action Buttons */}
            <Box sx={{ p: 2, borderTop: '1px solid #3a3d52', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                onClick={handleDeleteAndClose}
                sx={{
                  textTransform: 'none',
                  color: '#e0e0e0',
                  borderColor: '#94a3b8',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    backgroundColor: '#2d3048',
                  },
                }}
                variant="outlined"
              >
                {t('checkout.cancel_and_delete')}
              </Button>
              <Button 
                onClick={handleCancelClose}
                sx={{
                  textTransform: 'none',
                  color: '#e0e0e0',
                  borderColor: '#94a3b8',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    backgroundColor: '#2d3048',
                  },
                }}
                variant="outlined"
              >
                {t('checkout.cancel')}
              </Button>
              <Button 
                onClick={handleConfirmClose} 
                variant="contained"
                disabled={isSavingDraft}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#d32f2f',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  },
                  '&:disabled': {
                    backgroundColor: '#cccccc',
                    color: '#9ca3af',
                  },
                }}
              >
                {isSavingDraft
                  ? t('checkout.saving')
                  : t('checkout.save_draft_and_close')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* PayPal QR Payment Dialog - Rendered in parent (MinimalistDashboard) to stay visible when CheckoutDialog closes */}
    </>
  );
};

export default CheckoutDialog;

